import { WEBHOOK_SUBSCRIBE_EVENTS } from "@repo/contracts";
import type { ApiEnvelope, ApiPagination } from "@repo/shared-types";
import type {
  ClientDashboardStats,
  ClientOrgMembership,
  CreateOrderServiceRequest,
  DocumentUploadConfirmRequest,
  DocumentUploadIntentRequest,
  DocumentUploadIntentResponse,
  LogisticsAlertItem,
  LogisticsAlertsResponse,
  LogisticsDashboardStats,
  LogisticsExceptionItem,
  LogisticsExceptionKind,
  LogisticsExceptionsResponse,
  OrderService,
  OrderServiceAccessGrant,
  OrderServiceAccessView,
  OrderServiceActivityItem,
  OrderServiceAvailableAction,
  OrderServiceListItem,
  OrderServiceListResponse,
  BillingUploadLookupResponse,
  OrderServiceListSortField,
  OrderServiceStatus,
  WorkflowSlaStatus,
  WorkflowStep,
} from "@repo/shared-types/plugins/logistics-workflow";
import { mapOrderToDetailView } from "./map-order-detail";

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  realm: "staff" | "client";
  tenantId: string | null;
  permissions: string[];
  entitlements: Record<string, boolean | number | null>;
  /** Present on session bootstrap so portals can re-sync X-CSRF-Token. */
  csrfToken?: string;
  clientOrganizationId?: string | null;
  clientOrgRole?: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER" | null;
  clientOrgEngagement?: "DIRECT" | "AGENT" | null;
  permittedEndCustomerIds?: string[] | null;
  permittedStoreIds?: string[] | null;
};

/** Browser session after login/refresh — tokens live only in httpOnly cookies. */
export type AuthSession = {
  expiresIn: string;
  tokenType: "Bearer";
  csrfToken: string;
  user: {
    id: string;
    email: string;
    realm: "staff" | "client";
    tenantId: string | null;
  };
  activeTenantId: string | null;
  activeWorkspaceId: string | null;
};

/** @deprecated Use AuthSession — tokens are no longer returned to the browser. */
export type TokenPair = AuthSession & {
  accessToken?: string;
  refreshToken?: string;
};

export type LoginResult = AuthSession | { requires2fa: true; challengeId: string };

export type WorkspaceSummary = {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
  archivedAt?: string | null;
  memberCount?: number;
  addressDetails?: {
    line1: string;
    line2?: string | null;
    postalCode: string;
    city: string;
    state: string;
    country: string;
  } | null;
  phoneNumber?: string | null;
};

export type WorkspaceMemberRow = {
  id: string;
  userId: string;
  roleId: string;
  email: string;
  name: string;
  roleName: string;
};

export type StaffUserRow = {
  id: string;
  email: string;
  name: string;
  roleLabel: string;
  status: "active" | "invited" | "disabled";
  branchNames: string[];
  branchIds: string[];
};

export type InvitationRow = {
  id: string;
  email: string;
  roleLabel: string;
  status: "pending" | "accepted" | "expired";
  invitedAt: string;
};

export type AuditLogRow = {
  id: string;
  action: string;
  actorEmail: string;
  resource: string;
  resourceType?: string;
  resourceId?: string | null;
  actorUserId?: string | null;
  createdAt: string;
};

export type AuditLogListPage = {
  items: AuditLogRow[];
  nextCursor: string | null;
};

/** Cursor page lifted from `meta.pagination` (api-contract lists). */
export type CursorListPage<T> = {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
  limit: number;
};

export type SessionRow = {
  id: string;
  device: string;
  ip: string;
  lastActiveAt: string;
  current: boolean;
};

export type NotificationPrefCell = {
  typeCode: string;
  typeLabel: string;
  channel: "in_app" | "email" | "sms" | "push";
  enabled: boolean;
  transactional: boolean;
};

export type NotificationRow = {
  id: string;
  typeCode: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
};

export type ClientAnnouncementRow = {
  id: string;
  title: string;
  body: string;
  startsAt: string;
  readAt: string | null;
};

export type TicketRow = {
  id: string;
  subject: string;
  status: "open" | "in_progress" | "waiting" | "resolved" | "closed";
  priority: "low" | "normal" | "high";
  requesterName: string;
  assigneeUserId: string | null;
  assigneeName: string | null;
  updatedAt: string;
};

export type TicketMessage = {
  id: string;
  visibility: "internal" | "public";
  authorName: string;
  body: string;
  createdAt: string;
};

export type TicketDetail = TicketRow & { messages: TicketMessage[] };

export type AnnouncementRow = {
  id: string;
  title: string;
  body?: string;
  audience: "staff" | "client";
  status: "draft" | "published" | "archived";
  publishedAt: string | null;
};

export type ApiKeyRow = {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export type ApiKeyCreated = ApiKeyRow & { secret: string };

export type WebhookEndpoint = {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  consecutiveFailures: number;
};

export type RoleOption = {
  id: string;
  code: string;
  name: string;
  scope: string;
  isSystem?: boolean;
  permissionCodes?: string[];
};

export type PermissionOption = {
  id: string;
  code: string;
  description: string | null;
};

export type WebhookDelivery = {
  id: string;
  endpointId: string;
  eventName: string;
  status: "success" | "failed" | "pending";
  attempt: number;
  createdAt: string;
};

export type ConnectionRow = {
  id: string;
  category: "payment" | "messaging" | "logistics";
  providerKey: string;
  displayName: string;
  provisioningMode: "platform_managed" | "direct";
  tenantEnabled: boolean;
  platformSuspended: boolean;
  onboardingStatus?: "incomplete" | "restricted" | "complete";
  missingItems?: string[];
};

export type OrgProfile = {
  name: string;
  defaultLocale: string;
  defaultTimezone: string;
};

export type AiProviderRow = {
  id: string;
  name: string;
  enabled: boolean;
  models: { id: string; name: string; enabled: boolean }[];
};

export type PlatformEntitlementRow = {
  id: string;
  key: string;
  label: string;
  value: boolean | number | null;
};

export type EmailCategorySettingRow = {
  id: string;
  code: string;
  emailEnabled: boolean;
  locked: boolean;
  oncePerClientOrganizationPerDay: boolean;
};

export type CreateOrderServiceInput = CreateOrderServiceRequest;
export type DocumentUploadIntentInput = DocumentUploadIntentRequest;
export type DocumentUploadIntentResult = DocumentUploadIntentResponse;
export type DocumentUploadConfirmInput = DocumentUploadConfirmRequest;

export type UploadedDocumentRow = {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  orderServiceId: string | null;
  createdAt: string;
};

export type ClientOrganizationMemberRow = {
  id: string;
  clientOrganizationId: string;
  userId: string;
  email: string;
  name: string;
  role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
  createdAt: string;
  endCustomerIds?: string[];
  storeIds?: string[];
};

export type ClientUserCandidateRow = {
  id: string;
  email: string;
  name: string | null;
};

export type CreateClientOrganizationMemberInput = {
  userId: string;
  role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
  endCustomerIds?: string[];
  storeIds?: string[];
};

export type ClientOrganizationInvitationRow = {
  id: string;
  clientOrganizationId: string;
  email: string;
  role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
  endCustomerIds: string[];
  storeIds: string[];
  status: "pending" | "accepted" | "expired";
  invitedAt: string;
  expiresAt: string;
};

export type CreateClientOrganizationInvitationInput = {
  email: string;
  role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
  endCustomerIds?: string[];
  storeIds?: string[];
};

export type UpdateClientOrganizationMemberInput = {
  role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
  storeIds?: string[];
  endCustomerIds?: string[];
};

export type EndCustomerRow = CorporateProfileInput & {
  id: string;
  clientOrganizationId: string;
  name: string;
  code: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  storeCount?: number;
};

export type LogisticsDepartmentOption = { id: string; name: string };

export type OrderServiceListFilter =
  | "all"
  | "assigned_to_me"
  | "client"
  | "store"
  | "overdue"
  | "completed"
  | "my_tasks"
  | "team_tasks"
  | "unassigned"
  | "exceptions";

export type OrderServiceListRow = OrderServiceListItem & {
  departmentId?: string | null;
  assignedToMe?: boolean;
};

export type ClientRecentUpload = ClientDashboardStats["recentUploads"][number];

export type OrderServiceHistoryItem = {
  id: string;
  at: string;
  actorName: string;
  summary: string;
  action?: string;
  resourceType?: string;
  resourceId?: string | null;
  resourceLabel?: string | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  ip?: string | null;
};

export type ChecklistItemView = {
  id: string;
  documentTypeId: string;
  label: string;
  isMandatory: boolean;
  status: string;
  remarksText: string | null;
  isRemarksOnly: boolean;
  currentDocumentId: string | null;
  currentDocumentFilename: string | null;
};

export type MasterDocumentRow = {
  id: string;
  clientOrganizationId: string;
  endCustomerId: string | null;
  documentTypeId: string;
  filename: string;
  mimeType: string;
  size: number;
  version: number;
  issueDate: string | null;
  expiryDate: string | null;
  createdAt: string;
  uploadedById?: string | null;
  clientVisibility?: "INTERNAL" | "PUBLISHED" | null;
  canRelease?: boolean;
  releaseReturnReason?: string | null;
};

export type OrderServiceDetailView = OrderServiceListRow & {
  clientOrganizationId: string;
  logisticsBranchName: string;
  priority: string;
  customerReference: string | null;
  shipmentReference: string | null;
  remarks: string | null;
  shipmentInfo?: Record<string, unknown> | null;
  warehouse?: string | null;
  billingAccountId?: string | null;
  billingReference?: string | null;
  billingScope?: string | null;
  templateName?: string | null;
  createdById?: string;
  createdByName?: string | null;
  workflowInstanceId: string | null;
  checklistItems: ChecklistItemView[];
  missingDocuments: {
    id: string;
    label: string;
    isMandatory: boolean;
    documentTypeId: string;
  }[];
  /** Checklist items uploaded but not yet staff-verified. */
  pendingVerifyDocuments: {
    id: string;
    label: string;
    documentTypeId: string;
  }[];
  completedSteps: { id: string; name: string; completedAt: string | null }[];
  activeStep: {
    id: string;
    name: string;
    assigneeName: string | null;
    clientActionRequired: boolean;
    clientConsentRequired?: boolean;
    slaStatus: string;
    startedAt: string | null;
  } | null;
  /**
   * @deprecated Prefer `nextActionHint` + `availableActions` from Backend.
   * Kept for existing portal CTAs until OSD Phase 2 rebuild.
   */
  nextAction: {
    label: string;
    kind: "submit" | "step_status";
    stepId?: string;
    /** Target WorkflowStep status for kind=step_status (E.2 next hop). */
    toStatus?: string;
  } | null;
  /** Server-authored next-action copy (SRS §2.10). */
  nextActionHint?: string | null;
  /** Server-computed actions for the current actor. */
  availableActions?: OrderServiceAvailableAction[];
  /** Unified activity feed (do not FE-merge). */
  activity?: OrderServiceActivityItem[];
  displaySubtitle?: string | null;
  documentCount?: number;
  exceptionCount?: number;
  history: OrderServiceHistoryItem[];
  steps: {
    id: string;
    workflowInstanceId?: string;
    name: string;
    status: string;
    sequence: number;
    assigneeName: string | null;
    assigneeTeamName?: string | null;
    assigneeDepartmentName?: string | null;
    slaStatus?: string;
    clientActionRequired?: boolean;
    clientConsentRequired?: boolean;
    isRequired: boolean;
    canSkip: boolean;
    canReopen: boolean;
    canRepeat: boolean;
    startedAt?: string | null;
    completedAt?: string | null;
    approvalRequired: boolean;
    slaExpectedMinutes: number | null;
    slaWarningMinutes?: number | null;
    parallelGroupKey: string | null;
    documentTypeRequirements: {
      documentTypeId: string;
      mandatory: boolean;
    }[] | null;
  }[];
};

export type {
  OrderServiceActivityItem,
  OrderServiceAvailableAction,
  OrderServiceListSortField,
  LogisticsExceptionItem,
  LogisticsExceptionKind,
  LogisticsExceptionsResponse,
  LogisticsAlertItem,
  LogisticsAlertsResponse,
};

export type OrderServiceListPage = {
  items: OrderServiceListRow[];
  total: number;
  page: number;
  pageSize: number;
};

export type TicketListPage = {
  items: TicketRow[];
  nextCursor: string | null;
};

export type AssignStepInput = {
  assigneeType: "USER" | "TEAM" | "DEPARTMENT" | "UNASSIGNED";
  assigneeUserId?: string | null;
  assigneeTeamId?: string | null;
  assigneeDepartmentId?: string | null;
  reason?: string | null;
};

export type TeamOption = { id: string; name: string; type?: string };

export type BusinessAddress = {
  line1: string;
  line2?: string | null;
  postalCode: string;
  city: string;
  state: string;
  country: string;
};

export type CorporateProfileInput = {
  legalName?: string | null;
  contactPerson?: string | null;
  registrationNumber?: string | null;
  designation?: string | null;
  registeredAddress?: BusinessAddress | null;
  operationalAddress?: BusinessAddress | null;
  businessEmail?: string | null;
  supportPhone?: string | null;
};

export type ClientOrganizationRow = CorporateProfileInput & {
  id: string;
  name: string;
  code: string;
  engagement: "DIRECT" | "AGENT";
  status: "active" | "inactive";
  storeCount: number;
  memberCount: number;
};

export type StoreRow = {
  id: string;
  name: string;
  code: string;
  address: string | null;
  addressDetails?: BusinessAddress | null;
  businessEmail?: string | null;
  supportPhone?: string | null;
  clientOrganizationId: string;
  endCustomerId: string | null;
  endCustomerName: string | null;
  clientOrganizationName: string;
  status: "active" | "inactive";
};

export type WorkflowTemplateRow = {
  id: string;
  name: string;
  serviceTypeId: string | null;
  serviceTypeName: string;
  stepCount: number;
  version: number;
  isActive: boolean;
};

export type WorkflowTemplateDocumentRequirement = {
  documentTypeId: string;
  mandatory: boolean;
};

export type WorkflowTemplateStepDetail = {
  id: string;
  templateId: string;
  name: string;
  sequence: number;
  isRequired: boolean;
  clientActionRequired: boolean;
  clientConsentRequired: boolean;
  approvalRequired: boolean;
  defaultDepartmentId: string | null;
  defaultTeamId: string | null;
  defaultAssigneeUserId: string | null;
  slaExpectedMinutes: number | null;
  slaWarningMinutes: number | null;
  canSkip: boolean;
  canReopen: boolean;
  canRepeat: boolean;
  dependsOnStepIds: string[];
  parallelGroupKey: string | null;
  documentTypeRequirements: WorkflowTemplateDocumentRequirement[] | null;
  addOnServiceCode: string | null;
};

export type WorkflowTemplateDetail = {
  id: string;
  name: string;
  serviceTypeId: string | null;
  serviceTypeName: string;
  version: number;
  isActive: boolean;
  steps: WorkflowTemplateStepDetail[];
};

export type WorkflowTemplateStepInput = {
  id?: string;
  name: string;
  sequence: number;
  isRequired?: boolean;
  clientActionRequired?: boolean;
  clientConsentRequired?: boolean;
  approvalRequired?: boolean;
  defaultDepartmentId?: string | null;
  defaultTeamId?: string | null;
  defaultAssigneeUserId?: string | null;
  slaExpectedMinutes?: number | null;
  slaWarningMinutes?: number | null;
  canSkip?: boolean;
  canReopen?: boolean;
  canRepeat?: boolean;
  dependsOnStepIds?: string[];
  parallelGroupKey?: string | null;
  documentTypeRequirements?: WorkflowTemplateDocumentRequirement[] | null;
  addOnServiceCode?: string | null;
};

type WorkflowTemplateStepApi = {
  id: string;
  templateId?: string;
  name: string;
  sequence: number;
  isRequired: boolean;
  clientActionRequired?: boolean;
  clientConsentRequired?: boolean;
  approvalRequired?: boolean;
  defaultDepartmentId?: string | null;
  defaultTeamId?: string | null;
  defaultAssigneeUserId?: string | null;
  slaExpectedMinutes?: number | null;
  slaWarningMinutes?: number | null;
  canSkip: boolean;
  canReopen?: boolean;
  canRepeat?: boolean;
  dependsOnStepIds?: string[];
  parallelGroupKey?: string | null;
  documentTypeRequirements?: WorkflowTemplateDocumentRequirement[] | null;
  addOnServiceCode?: string | null;
};

function mapWorkflowTemplateStep(
  s: WorkflowTemplateStepApi,
  templateId: string,
): WorkflowTemplateStepDetail {
  return {
    id: s.id,
    templateId: s.templateId ?? templateId,
    name: s.name,
    sequence: s.sequence,
    isRequired: s.isRequired,
    clientActionRequired: s.clientActionRequired ?? false,
    clientConsentRequired: s.clientConsentRequired ?? false,
    approvalRequired: s.approvalRequired ?? false,
    defaultDepartmentId: s.defaultDepartmentId ?? null,
    defaultTeamId: s.defaultTeamId ?? null,
    defaultAssigneeUserId: s.defaultAssigneeUserId ?? null,
    slaExpectedMinutes: s.slaExpectedMinutes ?? null,
    slaWarningMinutes: s.slaWarningMinutes ?? null,
    canSkip: s.canSkip,
    canReopen: s.canReopen ?? false,
    canRepeat: s.canRepeat ?? false,
    dependsOnStepIds: s.dependsOnStepIds ?? [],
    parallelGroupKey: s.parallelGroupKey ?? null,
    documentTypeRequirements: s.documentTypeRequirements ?? null,
    addOnServiceCode: s.addOnServiceCode ?? null,
  };
}

export type DocumentTypeRow = {
  id: string;
  code: string;
  name: string;
  category: string;
  isActive: boolean;
  isRemarksOnly?: boolean;
};

export type ServiceTypeRow = {
  id: string;
  code: string;
  name: string;
  defaultTemplateId: string | null;
  defaultTemplateName: string | null;
  runningNumberFormat: string;
  isActive: boolean;
};

export type TeamRow = {
  id: string;
  name: string;
  type: string;
  parentTeamId: string | null;
  memberCount: number;
};

export type BillingAccountRow = {
  id: string;
  clientOrganizationId: string;
  storeId: string | null;
  name: string;
  externalReference: string | null;
  scope: "ORGANIZATION" | "STORE";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CommentRow = {
  id: string;
  orderServiceId: string;
  workflowStepId: string | null;
  userId: string;
  message: string;
  kind: "COMMENT" | "DOCUMENT_REQUEST" | "CLOSE_REQUEST";
  /** Tenant Staff only — API never returns true to client realm. */
  isInternal: boolean;
  checklistItemId: string | null;
  createdAt: string;
  authorName?: string | null;
};

export type CommentPresetRow = {
  id: string;
  title: string;
  body: string;
  kind: "COMMENT" | "DOCUMENT_REQUEST" | "CLOSE_REQUEST" | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type ApprovalRow = {
  id: string;
  workflowStepId: string;
  orderServiceId: string;
  status: string;
  requestedById: string;
  approverUserId: string | null;
  approverRole: string | null;
  decidedAt: string | null;
  reason: string | null;
  attemptNumber: number;
  createdAt: string;
  updatedAt: string;
};

export type ClientConsentRow = {
  id: string;
  workflowStepId: string;
  orderServiceId: string;
  status: "PENDING" | "AGREED" | "REJECTED" | "WITHDRAWN";
  title: string;
  summary: string;
  expectedBusinessDays: number;
  penaltyTerms: string;
  requestedById: string;
  decidedById: string | null;
  decidedAt: string | null;
  rejectionReason: string | null;
  attemptNumber: number;
  createdAt: string;
  updatedAt: string;
};

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details: Record<string, unknown> = {},
    public readonly requestId: string | null = null,
    public readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError && err.message) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export { WEBHOOK_SUBSCRIBE_EVENTS };

export type CreateApiClientOptions = {
  baseUrl: string;
  /**
   * Optional Bearer override for non-browser clients / tests.
   * Portal cookie mode leaves this unset so the browser sends httpOnly cookies.
   */
  getAccessToken?: () => string | null;
  /** Realm used for POST /v1/auth/refresh (cookie session). */
  getAuthRealm?: () => "staff" | "client" | null;
  getWorkspaceId?: () => string | null;
  /** CSRF token from AuthSession JSON (double-submit with httpOnly csrf cookie). */
  getCsrfToken?: () => string | null;
  /** Persist CSRF echoed from GET /settings/session (header/cookie re-sync). */
  onCsrfSynced?: (csrfToken: string) => void;
  /** True when a cookie session is expected (enables silent refresh). */
  hasSession?: () => boolean;
  /** Access cookie near expiry (from AuthSession.expiresIn wall clock). */
  accessNearExpiry?: () => boolean;
  /** Called after a successful silent refresh so the provider can persist csrf/realm. */
  onTokensRefreshed?: (pair: AuthSession) => void;
  /**
   * Auth rejected or silent refresh failed.
   * `refreshFailed` means credentials are unusable — clear the local session.
   */
  onUnauthorized?: (
    path: string,
    meta?: { refreshFailed?: boolean },
  ) => void;
};

/** Paths that must not trigger silent refresh (avoid loops / clearing mid-login). */
export function shouldAttemptTokenRefresh(path: string): boolean {
  const exempt = [
    "/auth/refresh",
    "/auth/staff/login",
    "/auth/client/login",
    "/auth/2fa/verify",
    "/auth/logout",
    "/auth/password/forgot",
    "/client-organization-invitations/preview",
    "/client-organization-invitations/accept",
  ];
  return !exempt.some((fragment) => path.includes(fragment));
}

/**
 * Decode JWT `exp` without verifying the signature.
 * Returns true when missing/malformed, already expired, or within `skewSeconds` of expiry.
 * Needed because the API maps expired access tokens to HTTP 404 (realm opacity),
 * so the client cannot rely on 401 alone to trigger silent refresh.
 */
export function accessTokenNeedsRefresh(
  token: string,
  skewSeconds = 60,
): boolean {
  const exp = readJwtExpSeconds(token);
  if (exp == null) return true;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
}

function readJwtExpSeconds(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const json = JSON.parse(base64UrlDecode(parts[1]!)) as { exp?: unknown };
    return typeof json.exp === "number" && Number.isFinite(json.exp)
      ? json.exp
      : null;
  } catch {
    return null;
  }
}

function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  const pad =
    normalized.length % 4 === 0
      ? ""
      : "=".repeat(4 - (normalized.length % 4));
  const binary = atob(normalized + pad);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export { suggestNextStepStatus } from "./suggest-next-step-status";
export { mapOrderToDetailView } from "./map-order-detail";

function filterToListQuery(filter?: OrderServiceListFilter): {
  status?: OrderServiceStatus;
  overdue?: boolean;
  assignee?: "me" | "team" | "unassigned";
  filter?: OrderServiceListFilter;
} {
  if (!filter || filter === "all") return {};
  if (filter === "completed") return { status: "COMPLETED", filter };
  if (filter === "overdue") return { overdue: true, filter };
  if (filter === "client") return { status: "WAITING_CLIENT", filter };
  if (filter === "store") return { status: "WAITING_LOGISTICS", filter };
  if (filter === "assigned_to_me" || filter === "my_tasks") {
    return { assignee: "me", filter };
  }
  if (filter === "team_tasks") return { assignee: "team", filter };
  if (filter === "unassigned") return { assignee: "unassigned", filter };
  if (filter === "exceptions") return { filter: "exceptions" };
  return { filter };
}

function normalizeTicketStatus(
  status: string,
): TicketRow["status"] {
  if (
    status === "resolved" ||
    status === "closed" ||
    status === "open" ||
    status === "in_progress" ||
    status === "waiting"
  ) {
    return status;
  }
  // Legacy mock / UI alias
  if (status === "pending") return "in_progress";
  return "open";
}

function normalizeTicketPriority(
  priority: string,
): TicketRow["priority"] {
  if (priority === "low" || priority === "normal" || priority === "high") {
    return priority;
  }
  return "high";
}

function platformFeatureLabel(key: string): string {
  if (key === "feature.logistics-workflow.auto_order_service_number") {
    return "Order Service Auto Numbering";
  }
  return key
    .replace(/^feature\.module\./, "")
    .replace(/^feature\./, "")
    .replaceAll("_", " ")
    .replaceAll(".", " ");
}

export function createApiClient(options: CreateApiClientOptions) {
  let refreshInFlight: Promise<boolean> | null = null;
  let csrfResyncInFlight: Promise<boolean> | null = null;

  /** Pull CSRF from session cookie echo without going through request() (no loop). */
  async function resyncCsrfFromSession(): Promise<boolean> {
    if (!csrfResyncInFlight) {
      csrfResyncInFlight = (async () => {
        try {
          const res = await fetch(`${options.baseUrl}/v1/settings/session`, {
            method: "GET",
            credentials: "include",
            headers: { Accept: "application/json" },
          });
          if (!res.ok) return false;
          const body = (await res.json()) as ApiEnvelope<{ csrfToken?: string }>;
          const token = body.data?.csrfToken;
          if (!body.success || typeof token !== "string" || !token) return false;
          options.onCsrfSynced?.(token);
          return true;
        } catch {
          return false;
        } finally {
          csrfResyncInFlight = null;
        }
      })();
    }
    return csrfResyncInFlight;
  }

  async function refreshAccessToken(): Promise<boolean> {
    const realm = options.getAuthRealm?.();
    if (realm !== "staff" && realm !== "client") {
      return false;
    }
    try {
      const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      const csrf = options.getCsrfToken?.();
      if (csrf) headers["X-CSRF-Token"] = csrf;
      const res = await fetch(`${options.baseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ realm }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as ApiEnvelope<AuthSession>;
      if (!body.success || !body.data?.csrfToken || !body.data.user) return false;
      options.onTokensRefreshed?.(body.data);
      return true;
    } catch {
      return false;
    }
  }

  function refreshAccessTokenSingleFlight(): Promise<boolean> {
    if (!refreshInFlight) {
      refreshInFlight = refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  async function requestWithMeta<T>(
    path: string,
    init: RequestInit & { parseJson?: boolean } = {},
    retried = false,
  ): Promise<{
    data: T;
    pagination: ApiPagination | null;
    requestId: string | null;
  }> {
    const { parseJson = true, ...fetchInit } = init;
    const headers = new Headers(fetchInit.headers);
    headers.set("Accept", "application/json");
    if (fetchInit.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    // Cookie access JWT is opaque to JS; refresh proactively from AuthSession TTL.
    if (
      !retried &&
      shouldAttemptTokenRefresh(path) &&
      options.hasSession?.() &&
      options.accessNearExpiry?.()
    ) {
      const refreshed = await refreshAccessTokenSingleFlight();
      if (!refreshed) {
        options.onUnauthorized?.(path, { refreshFailed: true });
        throw new ApiClientError(
          "AUTH_SESSION_EXPIRED",
          "Session expired. Please sign in again.",
          401,
        );
      }
    }

    // Optional Bearer override for scripts/tests; portal cookie mode skips this.
    const token = options.getAccessToken?.();
    if (token && token !== "cookie") {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const csrf = options.getCsrfToken?.();
    if (csrf) headers.set("X-CSRF-Token", csrf);
    const workspaceId = options.getWorkspaceId?.();
    if (workspaceId) headers.set("X-Workspace-Id", workspaceId);
    // Propagate ids for API access logs / Sentry correlation (browser crypto).
    if (!headers.has("X-Request-Id") && typeof crypto !== "undefined" && "randomUUID" in crypto) {
      const requestId = crypto.randomUUID();
      headers.set("X-Request-Id", requestId);
      if (!headers.has("X-Correlation-Id")) {
        headers.set("X-Correlation-Id", requestId);
      }
    }

    const res = await fetch(`${options.baseUrl}${path}`, {
      ...fetchInit,
      headers,
      credentials: "include",
    });

    if (res.status === 401) {
      if (!retried && shouldAttemptTokenRefresh(path)) {
        const refreshed = await refreshAccessTokenSingleFlight();
        if (refreshed) {
          return requestWithMeta<T>(path, init, true);
        }
        options.onUnauthorized?.(path, { refreshFailed: true });
      } else {
        options.onUnauthorized?.(path);
      }
    }

    if (res.status === 204 || res.status === 205) {
      return {
        data: undefined as T,
        pagination: null,
        requestId: res.headers.get("X-Request-Id"),
      };
    }

    const responseText = await res.text();
    if (!parseJson || !responseText) {
      if (!res.ok) {
        throw new ApiClientError(
          "HTTP_ERROR",
          res.statusText || "Request failed",
          res.status,
        );
      }
      return {
        data: undefined as T,
        pagination: null,
        requestId: res.headers.get("X-Request-Id"),
      };
    }

    let body: ApiEnvelope<T>;
    try {
      body = JSON.parse(responseText) as ApiEnvelope<T>;
    } catch {
      throw new ApiClientError(
        "INVALID_JSON",
        "Response is not JSON",
        res.status,
      );
    }

    if (!body.success) {
      if (
        !retried &&
        body.error?.code === "CSRF_TOKEN_INVALID" &&
        options.hasSession?.()
      ) {
        const synced = await resyncCsrfFromSession();
        if (synced) {
          return requestWithMeta<T>(path, init, true);
        }
      }
      const retryAfterHeader = res.headers.get("Retry-After");
      const retryAfterFromHeader = retryAfterHeader
        ? Number(retryAfterHeader)
        : Number.NaN;
      const retryAfterFromDetails = body.error?.details?.retryAfterSeconds;
      const retryAfterSeconds = Number.isFinite(retryAfterFromHeader)
        ? retryAfterFromHeader
        : typeof retryAfterFromDetails === "number" &&
            Number.isFinite(retryAfterFromDetails)
          ? retryAfterFromDetails
          : null;
      throw new ApiClientError(
        body.error?.code ?? "UNKNOWN",
        body.error?.message ?? "Request failed",
        res.status,
        body.error?.details ?? {},
        body.meta?.requestId ?? res.headers.get("X-Request-Id"),
        retryAfterSeconds,
      );
    }
    return {
      data: body.data,
      pagination: body.meta?.pagination ?? null,
      requestId: body.meta?.requestId ?? res.headers.get("X-Request-Id"),
    };
  }

  async function request<T>(
    path: string,
    init: RequestInit & { parseJson?: boolean } = {},
    retried = false,
  ): Promise<T> {
    const { data } = await requestWithMeta<T>(path, init, retried);
    return data;
  }

  function cursorPageFromMeta<T>(
    items: T[],
    pagination: ApiPagination | null,
    fallbackLimit: number,
  ): CursorListPage<T> {
    return {
      items,
      nextCursor: pagination?.nextCursor ?? null,
      hasMore: pagination?.hasMore ?? false,
      limit: pagination?.limit ?? fallbackLimit,
    };
  }

  async function getTicketDetail(
    id: string,
    path = `/v1/tickets/${id}`,
  ): Promise<TicketDetail> {
    const t = await request<{
      id: string;
      subject: string;
      status: string;
      priority: string;
      requesterUserId: string;
      assigneeUserId: string | null;
      requesterName?: string;
      assigneeName?: string | null;
      updatedAt: string | Date;
      messages?: Array<{
        id: string;
        body: string;
        isInternalNote: boolean;
        authorUserId: string;
        authorName?: string;
        createdAt: string | Date;
      }>;
    }>(path);
    return {
      id: t.id,
      subject: t.subject,
      status: normalizeTicketStatus(t.status),
      priority: normalizeTicketPriority(t.priority),
      requesterName: t.requesterName ?? t.requesterUserId,
      assigneeUserId: t.assigneeUserId ?? null,
      assigneeName: t.assigneeName ?? t.assigneeUserId,
      updatedAt:
        typeof t.updatedAt === "string"
          ? t.updatedAt
          : new Date(t.updatedAt).toISOString(),
      messages: (t.messages ?? []).map(
        (m): TicketMessage => ({
          id: m.id,
          visibility: m.isInternalNote ? "internal" : "public",
          authorName: m.authorName ?? m.authorUserId,
          body: m.body,
          createdAt:
            typeof m.createdAt === "string"
              ? m.createdAt
              : new Date(m.createdAt).toISOString(),
        }),
      ),
    };
  }

  return {
    request,
    auth: {
      staffLogin: (input: { email: string; password: string }) =>
        request<LoginResult>("/v1/auth/staff/login", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      clientLogin: (input: {
        email: string;
        password: string;
        tenantId?: string;
      }) =>
        request<LoginResult>("/v1/auth/client/login", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      login: async (input: {
        email: string;
        password: string;
        realm: "staff" | "client";
        tenantId?: string;
      }) => {
        if (input.realm === "staff") {
          return request<LoginResult>("/v1/auth/staff/login", {
            method: "POST",
            body: JSON.stringify({
              email: input.email,
              password: input.password,
            }),
          });
        }
        return request<LoginResult>("/v1/auth/client/login", {
          method: "POST",
          body: JSON.stringify({
            email: input.email,
            password: input.password,
            tenantId: input.tenantId,
          }),
        });
      },
      refresh: (input: { realm: "staff" | "client"; refreshToken?: string }) =>
        request<AuthSession>("/v1/auth/refresh", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      logout: (input?: { realm?: "staff" | "client"; refreshToken?: string }) =>
        request<{ ok: true }>("/v1/auth/logout", {
          method: "POST",
          body: JSON.stringify(input ?? {}),
        }),
      session: () => request<SessionUser>("/v1/settings/session"),
      me: () => request<SessionUser>("/v1/settings/session"),
      forgotPassword: (input: {
        email: string;
        realm: "staff" | "client";
        tenantId?: string;
      }) =>
        request<{ ok: true }>("/v1/auth/password/forgot", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      resetPassword: (input: { token: string; newPassword: string }) =>
        request<{ ok: true }>("/v1/auth/password/reset", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      verifyEmail: (token: string) =>
        request<{ ok: true }>("/v1/auth/email/verify", {
          method: "POST",
          body: JSON.stringify({ token }),
        }),
      changePassword: (input: {
        currentPassword: string;
        newPassword: string;
      }) =>
        request<{ ok: true }>("/v1/settings/password", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      verify2fa: (input: { challengeId: string; code: string }) =>
        request<AuthSession>("/v1/auth/2fa/verify", {
          method: "POST",
          body: JSON.stringify(input),
        }),
    },
    profile: {
      get: () =>
        request<{
          id: string;
          email: string;
          fullName: string | null;
          mobile: string | null;
          locale: string | null;
          timezone: string | null;
          roles: string[];
          teams: string[];
          departments: string[];
        }>("/v1/settings/profile"),
      update: (input: {
        fullName?: string | null;
        mobile?: string | null;
        locale?: string | null;
        timezone?: string | null;
      }) =>
        request<{
          id: string;
          fullName: string | null;
          mobile: string | null;
          locale: string | null;
          timezone: string | null;
        }>("/v1/settings/profile", {
          method: "PATCH",
          body: JSON.stringify(input),
        }),
    },
    sessions: {
      list: async () => {
        const rows = await request<
          Array<{
            id: string;
            userAgent: string | null;
            ip: string | null;
            createdAt: string | Date;
            expiresAt: string | Date;
          }>
        >("/v1/settings/sessions");
        return rows.map(
          (r, index): SessionRow => ({
            id: r.id,
            device: r.userAgent?.trim() || "Unknown device",
            ip: r.ip ?? "—",
            lastActiveAt:
              typeof r.createdAt === "string"
                ? r.createdAt
                : new Date(r.createdAt).toISOString(),
            // Nest has no "current" flag; treat the newest session as current.
            current: index === 0,
          }),
        );
      },
      revoke: (id: string) =>
        request<{ ok: true }>(`/v1/settings/sessions/${id}`, {
          method: "DELETE",
        }),
    },
    twoFactor: {
      status: () =>
        request<{
          enabled: boolean;
          method: "totp" | "email" | null;
          methods: Array<"totp" | "email">;
        }>("/v1/settings/2fa"),
      enable: () =>
        request<{ secret: string; otpauthUrl: string }>(
          "/v1/settings/2fa/totp/setup",
          { method: "POST" },
        ),
      confirm: (input: { code: string }) =>
        request<{ backupCodes: string[] | null }>(
          "/v1/settings/2fa/totp/confirm",
          { method: "POST", body: JSON.stringify(input) },
        ),
      disable: (
        method: "totp" | "email" = "totp",
        input?: { password?: string },
      ) =>
        request<{ ok: true }>(`/v1/settings/2fa/${method}`, {
          method: "DELETE",
          body: JSON.stringify(input ?? {}),
        }),
    },
    notificationPreferences: {
      list: async () => {
        const res = await request<{
          types: Array<{
            code: string;
            isTransactional: boolean;
            channels: Array<{ channel: string; enabled: boolean }>;
          }>;
        }>("/v1/settings/notification-preferences");
        const cells: NotificationPrefCell[] = [];
        for (const type of res.types ?? []) {
          for (const ch of type.channels ?? []) {
            if (
              ch.channel !== "in_app" &&
              ch.channel !== "email" &&
              ch.channel !== "sms" &&
              ch.channel !== "push"
            ) {
              continue;
            }
            cells.push({
              typeCode: type.code,
              typeLabel: type.code,
              channel: ch.channel,
              enabled: ch.enabled,
              transactional: type.isTransactional,
            });
          }
        }
        return cells;
      },
      update: (input: {
        preferences: Array<{
          typeCode: string;
          channel: string;
          enabled: boolean;
        }>;
      }) =>
        request<{ ok: true }>("/v1/settings/notification-preferences", {
          method: "PUT",
          body: JSON.stringify(input),
        }),
    },
    notifications: {
      list: async (query?: {
        cursor?: string;
        limit?: number;
      }): Promise<CursorListPage<NotificationRow>> => {
        const params = new URLSearchParams();
        if (query?.cursor) params.set("cursor", query.cursor);
        if (query?.limit) params.set("limit", String(query.limit));
        const qs = params.toString();
        const fallbackLimit = query?.limit ?? 20;
        const { data, pagination } = await requestWithMeta<
          Array<{
            id: string;
            typeCode: string;
            title: string;
            body: string;
            readAt: string | Date | null;
            createdAt: string | Date;
          }>
        >(`/v1/notifications${qs ? `?${qs}` : ""}`);
        const items = (data ?? []).map(
          (r): NotificationRow => ({
            id: r.id,
            typeCode: r.typeCode,
            title: r.title,
            body: r.body,
            readAt: r.readAt
              ? typeof r.readAt === "string"
                ? r.readAt
                : r.readAt.toISOString()
              : null,
            createdAt:
              typeof r.createdAt === "string"
                ? r.createdAt
                : new Date(r.createdAt).toISOString(),
          }),
        );
        return cursorPageFromMeta(items, pagination, fallbackLimit);
      },
      markRead: (id: string) =>
        request<{ ok: true }>(`/v1/notifications/${id}/read`, {
          method: "POST",
        }),
    },
    organization: {
      get: async () => {
        const org = await request<{
          name: string;
          branding?: Record<string, unknown> | null;
        }>("/v1/organization");
        const branding = (org.branding ?? {}) as Record<string, unknown>;
        return {
          name: org.name,
          defaultLocale:
            typeof branding.defaultLocale === "string"
              ? branding.defaultLocale
              : "zh-CN",
          defaultTimezone:
            typeof branding.defaultTimezone === "string"
              ? branding.defaultTimezone
              : "Asia/Kuala_Lumpur",
        } satisfies OrgProfile;
      },
      update: async (input: Partial<OrgProfile>) => {
        const branding: Record<string, string> = {};
        if (input.defaultLocale !== undefined) {
          branding.defaultLocale = input.defaultLocale;
        }
        if (input.defaultTimezone !== undefined) {
          branding.defaultTimezone = input.defaultTimezone;
        }
        const org = await request<{
          name: string;
          branding?: Record<string, unknown> | null;
        }>("/v1/organization", {
          method: "PATCH",
          body: JSON.stringify({
            ...(input.name !== undefined ? { name: input.name } : {}),
            ...(Object.keys(branding).length > 0 ? { branding } : {}),
          }),
        });
        const b = (org.branding ?? {}) as Record<string, unknown>;
        return {
          name: org.name,
          defaultLocale:
            typeof b.defaultLocale === "string" ? b.defaultLocale : "zh-CN",
          defaultTimezone:
            typeof b.defaultTimezone === "string"
              ? b.defaultTimezone
              : "Asia/Kuala_Lumpur",
        } satisfies OrgProfile;
      },
    },
    workspaces: {
      list: async () => {
        const rows = await request<
          Array<{
            id: string;
            name: string;
            slug: string;
            isDefault: boolean;
            archivedAt?: string | null;
            memberCount?: number;
            addressDetails?: WorkspaceSummary["addressDetails"];
            phoneNumber?: string | null;
          }>
        >("/v1/workspaces");
        return rows.map(
          (w): WorkspaceSummary => ({
            id: w.id,
            name: w.name,
            slug: w.slug,
            isDefault: w.isDefault,
            archivedAt: w.archivedAt ?? null,
            memberCount: w.memberCount ?? 0,
            addressDetails: w.addressDetails ?? null,
            phoneNumber: w.phoneNumber ?? null,
          }),
        );
      },
      create: (input: { name: string; slug?: string; code?: string; addressDetails?: WorkspaceSummary["addressDetails"]; phoneNumber?: string | null }) =>
        request<WorkspaceSummary>("/v1/workspaces", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      update: (
        id: string,
        input: { name?: string; slug?: string; code?: string; addressDetails?: WorkspaceSummary["addressDetails"]; phoneNumber?: string | null },
      ) =>
        request<WorkspaceSummary>(`/v1/workspaces/${id}`, {
          method: "PATCH",
          body: JSON.stringify(input),
        }),
      archive: (id: string) =>
        request<unknown>(`/v1/workspaces/${id}/archive`, { method: "POST" }),
      switch: (input: { workspaceId: string; refreshToken?: string }) =>
        request<AuthSession>("/v1/workspaces/switch", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      listMembers: async (workspaceId: string) => {
        const rows = await request<
          Array<{
            id: string;
            userId: string;
            roleId: string;
            user?: {
              id: string;
              email: string;
              fullName: string | null;
            };
            role?: { id: string; name: string; code?: string };
          }>
        >(`/v1/workspaces/${workspaceId}/members`);
        return rows.map(
          (r): WorkspaceMemberRow => ({
            id: r.id,
            userId: r.userId,
            roleId: r.roleId,
            email: r.user?.email ?? r.userId,
            name: r.user?.fullName ?? r.user?.email ?? r.userId,
            roleName: r.role?.name ?? r.role?.code ?? r.roleId,
          }),
        );
      },
      addMember: (
        workspaceId: string,
        body: { userId: string; roleId: string },
      ) =>
        request(`/v1/workspaces/${workspaceId}/members`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      updateMember: (
        workspaceId: string,
        userId: string,
        body: { roleId: string },
      ) =>
        request(`/v1/workspaces/${workspaceId}/members/${userId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),
      removeMember: (workspaceId: string, userId: string) =>
        request(`/v1/workspaces/${workspaceId}/members/${userId}`, {
          method: "DELETE",
        }),
    },
    users: {
      list: async (query?: { q?: string }) => {
        const qs = query?.q ? `?q=${encodeURIComponent(query.q)}` : "";
        const rows = await request<
          Array<StaffUserRow & { branchIds?: string[] }>
        >(`/v1/members${qs}`);
        return rows.map(
          (row): StaffUserRow => ({
            ...row,
            branchNames: row.branchNames ?? [],
            branchIds: row.branchIds ?? [],
          }),
        );
      },
      create: async (body: {
        email: string;
        fullName: string;
        password: string;
        roleId: string;
        workspaceId?: string | null;
      }) => {
        const row = await request<StaffUserRow & { branchIds?: string[] }>(
          "/v1/members",
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
        return {
          ...row,
          branchNames: row.branchNames ?? [],
          branchIds: row.branchIds ?? [],
        } satisfies StaffUserRow;
      },
      changeRole: (userId: string, roleId: string) =>
        request(`/v1/members/${userId}/roles`, {
          method: "PATCH",
          body: JSON.stringify({ roleId }),
        }),
      resetPassword: (userId: string, newPassword: string) =>
        request<{ ok: true }>(`/v1/members/${userId}/password`, {
          method: "POST",
          body: JSON.stringify({ newPassword }),
        }),
      replaceWorkspaces: (
        userId: string,
        body: { workspaceIds: string[] },
      ) =>
        request<{ branchIds: string[]; branchNames: string[] }>(
          `/v1/members/${userId}/workspaces`,
          {
            method: "PUT",
            body: JSON.stringify(body),
          },
        ),
    },
    roles: {
      list: () =>
        request<
          Array<{
            id: string;
            code: string;
            name: string;
            scope: string;
            isSystem?: boolean;
            permissionCodes?: string[];
          }>
        >("/v1/roles").then((rows) =>
          rows.map((r) => ({
            id: r.id,
            code: r.code,
            name: r.name,
            scope: r.scope,
            isSystem: r.isSystem,
            permissionCodes: r.permissionCodes ?? [],
          })),
        ),
      create: (input: { code: string; name: string; scope: "tenant" | "workspace"; permissionIds: string[] }) =>
        request<RoleOption>("/v1/roles", { method: "POST", body: JSON.stringify(input) }),
      update: (id: string, input: { code?: string; name?: string; scope?: "tenant" | "workspace"; permissionIds?: string[] }) =>
        request<RoleOption>(`/v1/roles/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
      remove: (id: string) => request(`/v1/roles/${id}`, { method: "DELETE" }),
    },
    permissions: {
      list: () =>
        request<PermissionOption[]>("/v1/permissions").then((rows) =>
          rows.map((p) => ({
            id: p.id,
            code: p.code,
            description: p.description ?? null,
          })),
        ),
    },
    teams: {
      list: async () => {
        const rows = await request<
          Array<{
            id: string;
            name: string;
            type: string;
            parentTeamId?: string | null;
            members?: unknown[];
          }>
        >("/v1/teams");
        return rows.map(
          (t): TeamRow => ({
            id: t.id,
            name: t.name,
            type: t.type,
            parentTeamId: t.parentTeamId ?? null,
            memberCount: Array.isArray(t.members) ? t.members.length : 0,
          }),
        );
      },
      create: (input: {
        name: string;
        type?: string;
        parentTeamId?: string | null;
      }) =>
        request<{
          id: string;
          name: string;
          type: string;
          parentTeamId: string | null;
        }>("/v1/teams", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      update: (
        id: string,
        input: {
          name?: string;
          type?: string;
          parentTeamId?: string | null;
        },
      ) =>
        request(`/v1/teams/${id}`, {
          method: "PATCH",
          body: JSON.stringify(input),
        }),
      remove: (id: string) =>
        request(`/v1/teams/${id}`, { method: "DELETE" }),
      listMembers: async (teamId: string) => {
        const rows = await request<
          Array<{
            id?: string;
            userId: string;
            user?: { id: string; email?: string; name?: string | null; fullName?: string | null };
          }>
        >(`/v1/teams/${teamId}/members`);
        return rows.map((m) => ({
          id: m.id ?? m.userId,
          userId: m.userId,
          email: m.user?.email ?? m.userId,
          name: m.user?.name ?? m.user?.fullName ?? m.user?.email ?? m.userId,
        }));
      },
      addMember: (teamId: string, userId: string) =>
        request(`/v1/teams/${teamId}/members`, {
          method: "POST",
          body: JSON.stringify({ userId }),
        }),
      removeMember: (teamId: string, userId: string) =>
        request(`/v1/teams/${teamId}/members/${userId}`, {
          method: "DELETE",
        }),
      listRoles: async (teamId: string) => {
        const rows = await request<
          Array<{
            roleId: string;
            role?: { id: string; name: string; code?: string };
          }>
        >(`/v1/teams/${teamId}/roles`);
        return rows.map((r) => ({
          id: r.roleId,
          name: r.role?.name ?? r.role?.code ?? r.roleId,
        }));
      },
      assignRole: (teamId: string, roleId: string) =>
        request(`/v1/teams/${teamId}/roles`, {
          method: "POST",
          body: JSON.stringify({ roleId }),
        }),
      removeRole: (teamId: string, roleId: string) =>
        request(`/v1/teams/${teamId}/roles/${roleId}`, {
          method: "DELETE",
        }),
    },
    invitations: {
      list: () => request<InvitationRow[]>("/v1/invitations"),
      create: (input: {
        email: string;
        roleId: string;
        workspaceId?: string | null;
      }) =>
        request<InvitationRow>("/v1/invitations", {
          method: "POST",
          body: JSON.stringify(input),
        }),
      accept: (input: { token: string; password: string; fullName?: string }) =>
        request<{ ok: true }>("/v1/invitations/accept", {
          method: "POST",
          body: JSON.stringify(input),
        }),
    },
    auditLogs: {
      list: async (query?: {
        limit?: number;
        cursor?: string;
        action?: string;
        resourceType?: string;
        resourceId?: string;
        actorUserId?: string;
        from?: string;
        to?: string;
        q?: string;
      }): Promise<AuditLogListPage> => {
        const params = new URLSearchParams();
        if (query?.limit != null) params.set("limit", String(query.limit));
        if (query?.cursor) params.set("cursor", query.cursor);
        if (query?.action) params.set("action", query.action);
        if (query?.resourceType) params.set("resourceType", query.resourceType);
        if (query?.resourceId) params.set("resourceId", query.resourceId);
        if (query?.actorUserId) params.set("actorUserId", query.actorUserId);
        if (query?.from) params.set("from", query.from);
        if (query?.to) params.set("to", query.to);
        if (query?.q?.trim()) params.set("q", query.q.trim());
        const qs = params.toString() ? `?${params}` : "";
        const res = await request<{
          items: AuditLogRow[];
          nextCursor: string | null;
        }>(`/v1/settings/audit-logs${qs}`);
        return {
          items: res.items ?? [],
          nextCursor: res.nextCursor ?? null,
        };
      },
    },
    tickets: {
      list: async (query?: {
        q?: string;
        cursor?: string;
        limit?: number;
      }): Promise<TicketListPage> => {
        const params = new URLSearchParams();
        if (query?.q?.trim()) params.set("q", query.q.trim());
        if (query?.cursor) params.set("cursor", query.cursor);
        if (query?.limit != null) params.set("limit", String(query.limit));
        const qs = params.toString() ? `?${params}` : "";
        const res = await request<{
          items: Array<{
            id: string;
            subject: string;
            status: string;
            priority: string;
            requesterUserId: string;
            assigneeUserId: string | null;
            requesterName?: string;
            assigneeName?: string | null;
            updatedAt: string | Date;
          }>;
          nextCursor: string | null;
        }>(`/v1/tickets${qs}`);
        return {
          items: (res.items ?? []).map(
            (t): TicketRow => ({
              id: t.id,
              subject: t.subject,
              status: normalizeTicketStatus(t.status),
              priority: normalizeTicketPriority(t.priority),
              requesterName: t.requesterName ?? t.requesterUserId,
              assigneeUserId: t.assigneeUserId ?? null,
              assigneeName: t.assigneeName ?? t.assigneeUserId,
              updatedAt:
                typeof t.updatedAt === "string"
                  ? t.updatedAt
                  : new Date(t.updatedAt).toISOString(),
            }),
          ),
          nextCursor: res.nextCursor ?? null,
        };
      },
      get: (id: string) => getTicketDetail(id),
      patch: async (
        id: string,
        input: {
          subject?: string;
          status?: string;
          priority?: string;
          assigneeUserId?: string | null;
        },
      ) => {
        await request(`/v1/tickets/${id}`, {
          method: "PATCH",
          body: JSON.stringify(input),
        });
        return getTicketDetail(id);
      },
      reply: async (
        id: string,
        input: { body: string; isInternalNote?: boolean },
      ) => {
        await request(`/v1/tickets/${id}/messages`, {
          method: "POST",
          body: JSON.stringify(input),
        });
        return getTicketDetail(id);
      },
      create: (input: {
        subject: string;
        body: string;
        priority?: string;
        assigneeUserId?: string | null;
      }) =>
        request<TicketRow>("/v1/tickets", {
          method: "POST",
          body: JSON.stringify(input),
        }),
    },
    announcements: {
      list: async () => {
        const rows = await request<
          Array<{
            id: string;
            title: string;
            body?: string;
            audience: "staff" | "client";
            status: string;
            publishedAt?: string | Date | null;
            createdAt?: string | Date;
          }>
        >("/v1/announcements");
        return rows.map(
          (r): AnnouncementRow => ({
            id: r.id,
            title: r.title,
            body: r.body ?? "",
            audience: r.audience,
            status:
              r.status === "published"
                ? "published"
                : r.status === "unpublished"
                  ? "archived"
                  : "draft",
            publishedAt: r.publishedAt
              ? typeof r.publishedAt === "string"
                ? r.publishedAt
                : new Date(r.publishedAt).toISOString()
              : null,
          }),
        );
      },
      publish: async (input: {
        title: string;
        body: string;
        audience: "staff" | "client";
        startsAt?: string;
        endsAt?: string | null;
        status?: "draft" | "published";
      }) => {
        const r = await request<{
          id: string;
          title: string;
          audience: "staff" | "client";
          status: string;
          publishedAt?: string | Date | null;
        }>("/v1/announcements", {
          method: "POST",
          body: JSON.stringify(input),
        });
        return {
          id: r.id,
          title: r.title,
          audience: r.audience,
          status: r.status === "published" ? "published" as const : "draft" as const,
          publishedAt: r.publishedAt
            ? typeof r.publishedAt === "string"
              ? r.publishedAt
              : new Date(r.publishedAt).toISOString()
            : new Date().toISOString(),
        } satisfies AnnouncementRow;
      },
      unpublish: async (id: string) => {
        await request(`/v1/announcements/${id}/unpublish`, { method: "POST" });
      },
      update: async (id: string, input: { title: string; body: string; audience: "staff" | "client"; startsAt?: string; endsAt?: string | null }) => {
        await request(`/v1/announcements/${id}`, { method: "PATCH", body: JSON.stringify(input) });
      },
      inbox: async () => {
        const rows = await request<
          Array<{
            id: string;
            title: string;
            body?: string;
            startsAt?: string | Date;
            publishedAt?: string | Date | null;
            readAt?: string | Date | null;
          }>
        >("/v1/announcements/inbox");
        return rows.map(
          (r): ClientAnnouncementRow => ({
            id: r.id,
            title: r.title,
            body: r.body ?? "",
            startsAt: r.startsAt
              ? typeof r.startsAt === "string"
                ? r.startsAt
                : new Date(r.startsAt).toISOString()
              : r.publishedAt
                ? typeof r.publishedAt === "string"
                  ? r.publishedAt
                  : new Date(r.publishedAt).toISOString()
                : new Date().toISOString(),
            readAt: r.readAt
              ? typeof r.readAt === "string"
                ? r.readAt
                : r.readAt.toISOString()
              : null,
          }),
        );
      },
      markRead: (id: string) =>
        request<{ ok: true }>(`/v1/announcements/${id}/read`, {
          method: "POST",
        }),
    },
    clientAnnouncements: {
      list: async () => {
        const rows = await request<
          Array<{
            id: string;
            title: string;
            body?: string;
            startsAt?: string | Date;
            publishedAt?: string | Date | null;
            readAt?: string | Date | null;
          }>
        >("/v1/client/announcements");
        return rows.map(
          (r): ClientAnnouncementRow => ({
            id: r.id,
            title: r.title,
            body: r.body ?? "",
            startsAt: r.startsAt
              ? typeof r.startsAt === "string"
                ? r.startsAt
                : new Date(r.startsAt).toISOString()
              : r.publishedAt
                ? typeof r.publishedAt === "string"
                  ? r.publishedAt
                  : new Date(r.publishedAt).toISOString()
                : new Date().toISOString(),
            readAt: r.readAt
              ? typeof r.readAt === "string"
                ? r.readAt
                : r.readAt.toISOString()
              : null,
          }),
        );
      },
      markRead: (id: string) =>
        request<{ ok: true }>(`/v1/client/announcements/${id}/read`, {
          method: "POST",
        }),
    },
    apiKeys: {
      list: async () => {
        const rows = await request<
          Array<{
            id: string;
            name: string;
            keyPrefix: string;
            scopes?: string[];
            createdAt: string | Date;
            lastUsedAt: string | Date | null;
          }>
        >("/v1/settings/api-keys");
        return rows.map(
          (r): ApiKeyRow => ({
            id: r.id,
            name: r.name,
            prefix: r.keyPrefix,
            scopes: Array.isArray(r.scopes) ? r.scopes : [],
            createdAt:
              typeof r.createdAt === "string"
                ? r.createdAt
                : new Date(r.createdAt).toISOString(),
            lastUsedAt: r.lastUsedAt
              ? typeof r.lastUsedAt === "string"
                ? r.lastUsedAt
                : new Date(r.lastUsedAt).toISOString()
              : null,
            revokedAt: null,
          }),
        );
      },
      create: async (input: { name: string; scopes: string[] }) => {
        const r = await request<{
          id: string;
          name: string;
          keyPrefix: string;
          scopes?: string[];
          createdAt: string | Date;
          lastUsedAt: string | Date | null;
          key: string;
        }>("/v1/settings/api-keys", {
          method: "POST",
          body: JSON.stringify(input),
        });
        return {
          id: r.id,
          name: r.name,
          prefix: r.keyPrefix,
          scopes: Array.isArray(r.scopes) ? r.scopes : input.scopes,
          createdAt:
            typeof r.createdAt === "string"
              ? r.createdAt
              : new Date(r.createdAt).toISOString(),
          lastUsedAt: r.lastUsedAt
            ? typeof r.lastUsedAt === "string"
              ? r.lastUsedAt
              : new Date(r.lastUsedAt).toISOString()
            : null,
          revokedAt: null,
          secret: r.key,
        } satisfies ApiKeyCreated;
      },
      revoke: (id: string) =>
        request<{ ok: true }>(`/v1/settings/api-keys/${id}`, {
          method: "DELETE",
        }),
      rotate: async (id: string) => {
        const r = await request<{
          id: string;
          name: string;
          keyPrefix: string;
          scopes?: string[];
          createdAt: string | Date;
          lastUsedAt: string | Date | null;
          key: string;
        }>(`/v1/settings/api-keys/${id}/rotate`, { method: "POST" });
        return {
          id: r.id,
          name: r.name,
          prefix: r.keyPrefix,
          scopes: Array.isArray(r.scopes) ? r.scopes : [],
          createdAt:
            typeof r.createdAt === "string"
              ? r.createdAt
              : new Date(r.createdAt).toISOString(),
          lastUsedAt: r.lastUsedAt
            ? typeof r.lastUsedAt === "string"
              ? r.lastUsedAt
              : new Date(r.lastUsedAt).toISOString()
            : null,
          revokedAt: null,
          secret: r.key,
        } satisfies ApiKeyCreated;
      },
    },
    webhooks: {
      listEndpoints: async () => {
        const rows = await request<
          Array<{
            id: string;
            url: string;
            subscribedEvents: string[];
            status: "active" | "disabled";
            consecutiveFailures: number;
          }>
        >("/v1/settings/webhooks/endpoints");
        return rows.map(
          (r): WebhookEndpoint => ({
            id: r.id,
            url: r.url,
            events: r.subscribedEvents,
            active: r.status === "active",
            consecutiveFailures: r.consecutiveFailures,
          }),
        );
      },
      createEndpoint: async (input: {
        url: string;
        subscribedEvents: string[];
        description?: string;
      }) => {
        const r = await request<{
          id: string;
          url: string;
          subscribedEvents: string[];
          status: "active" | "disabled";
          consecutiveFailures: number;
          secret?: string;
        }>("/v1/settings/webhooks/endpoints", {
          method: "POST",
          body: JSON.stringify(input),
        });
        return {
          id: r.id,
          url: r.url,
          events: r.subscribedEvents,
          active: r.status === "active",
          consecutiveFailures: r.consecutiveFailures,
        } satisfies WebhookEndpoint;
      },
      updateEndpoint: async (
        id: string,
        input: {
          url?: string;
          subscribedEvents?: string[];
          description?: string | null;
          status?: "active" | "disabled";
        },
      ) => {
        const r = await request<{
          id: string;
          url: string;
          subscribedEvents: string[];
          status: "active" | "disabled";
          consecutiveFailures: number;
        }>(`/v1/settings/webhooks/endpoints/${id}`, {
          method: "PATCH",
          body: JSON.stringify(input),
        });
        return {
          id: r.id,
          url: r.url,
          events: r.subscribedEvents,
          active: r.status === "active",
          consecutiveFailures: r.consecutiveFailures,
        } satisfies WebhookEndpoint;
      },
      deleteEndpoint: (id: string) =>
        request(`/v1/settings/webhooks/endpoints/${id}`, {
          method: "DELETE",
        }),
      rotateSecret: (id: string) =>
        request<{ secret: string }>(
          `/v1/settings/webhooks/endpoints/${id}/rotate-secret`,
          { method: "POST" },
        ),
      sendTest: (id: string) =>
        request<{ ok: true }>(`/v1/settings/webhooks/endpoints/${id}/ping`, {
          method: "POST",
        }),
      listDeliveries: async (endpointId: string) => {
        const rows = await request<
          Array<{
            id: string;
            endpointId: string;
            eventId: string;
            status: string;
            attempt: number;
            createdAt: string | Date;
          }>
        >(`/v1/settings/webhooks/endpoints/${endpointId}/deliveries`);
        return rows.map(
          (r): WebhookDelivery => ({
            id: r.id,
            endpointId: r.endpointId,
            eventName: r.eventId,
            status:
              r.status === "success" || r.status === "failed"
                ? r.status
                : "pending",
            attempt: r.attempt,
            createdAt:
              typeof r.createdAt === "string"
                ? r.createdAt
                : new Date(r.createdAt).toISOString(),
          }),
        );
      },
      replay: (deliveryId: string) =>
        request<{ ok: true }>(
          `/v1/settings/webhooks/deliveries/${deliveryId}/replay`,
          { method: "POST" },
        ),
    },
    connections: {
      list: async (category?: string) => {
        const qs = category ? `?category=${encodeURIComponent(category)}` : "";
        const rows = await request<
          Array<{
            id: string;
            category: string;
            providerCode: string;
            displayName: string;
            provisioningMode: string;
            tenantEnabled: boolean;
            platformDisabledAt: string | Date | null;
            onboardingStatus?: string;
          }>
        >(`/v1/settings/connections${qs}`);
        return rows.map(
          (r): ConnectionRow => ({
            id: r.id,
            category: r.category as ConnectionRow["category"],
            providerKey: r.providerCode,
            displayName: r.displayName,
            provisioningMode:
              r.provisioningMode as ConnectionRow["provisioningMode"],
            tenantEnabled: r.tenantEnabled,
            platformSuspended: Boolean(r.platformDisabledAt),
            onboardingStatus: r.onboardingStatus as
              | ConnectionRow["onboardingStatus"]
              | undefined,
          }),
        );
      },
      setEnabled: async (id: string, tenantEnabled: boolean) => {
        const r = await request<{
          id: string;
          category: string;
          providerCode: string;
          displayName: string;
          provisioningMode: string;
          tenantEnabled: boolean;
          platformDisabledAt: string | Date | null;
          onboardingStatus?: string;
        }>(`/v1/settings/connections/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ tenantEnabled }),
        });
        return {
          id: r.id,
          category: r.category as ConnectionRow["category"],
          providerKey: r.providerCode,
          displayName: r.displayName,
          provisioningMode:
            r.provisioningMode as ConnectionRow["provisioningMode"],
          tenantEnabled: r.tenantEnabled,
          platformSuspended: Boolean(r.platformDisabledAt),
          onboardingStatus: r.onboardingStatus as
            | ConnectionRow["onboardingStatus"]
            | undefined,
        } satisfies ConnectionRow;
      },
      create: async (input: {
        category: ConnectionRow["category"];
        providerCode: string;
        displayName: string;
        provisioningMode: ConnectionRow["provisioningMode"];
        credentials?: Record<string, unknown> | null;
        config?: Record<string, unknown> | null;
      }) => {
        const r = await request<{
          id: string;
          category: string;
          providerCode: string;
          displayName: string;
          provisioningMode: string;
          tenantEnabled: boolean;
          platformDisabledAt: string | Date | null;
          onboardingStatus?: string;
        }>("/v1/settings/connections", {
          method: "POST",
          body: JSON.stringify(input),
        });
        return {
          id: r.id,
          category: r.category as ConnectionRow["category"],
          providerKey: r.providerCode,
          displayName: r.displayName,
          provisioningMode:
            r.provisioningMode as ConnectionRow["provisioningMode"],
          tenantEnabled: r.tenantEnabled,
          platformSuspended: Boolean(r.platformDisabledAt),
          onboardingStatus: r.onboardingStatus as
            | ConnectionRow["onboardingStatus"]
            | undefined,
        } satisfies ConnectionRow;
      },
      test: (id: string) =>
        request<{ ok: boolean; message?: string }>(
          `/v1/settings/connections/${id}/test`,
          { method: "POST" },
        ),
    },
    platform: {
      entitlements: async (): Promise<PlatformEntitlementRow[]> => {
        const rows = await request<Array<{ key: string; value: boolean }>>("/v1/settings/platform/modules");
        return rows.map((row) => ({
          id: row.key,
          key: row.key,
          label: platformFeatureLabel(row.key),
          value: row.value,
        }));
      },
      updateEntitlement: (key: string, value: boolean) =>
        request<{ key: string; value: boolean }>(`/v1/settings/platform/modules/${encodeURIComponent(key)}`, {
          method: "PATCH",
          body: JSON.stringify({ value }),
        }),
      emailCategories: async (): Promise<EmailCategorySettingRow[]> => {
        const rows = await request<
          Array<{
            code: string;
            emailEnabled: boolean;
            locked: boolean;
            oncePerClientOrganizationPerDay: boolean;
          }>
        >("/v1/settings/platform/email-categories");
        return rows.map((row) => ({ ...row, id: row.code }));
      },
      updateEmailCategory: (code: string, emailEnabled: boolean) =>
        request<{
          code: string;
          emailEnabled: boolean;
          locked: boolean;
          oncePerClientOrganizationPerDay: boolean;
        }>(
          `/v1/settings/platform/email-categories/${encodeURIComponent(code)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ emailEnabled }),
          },
        ),
      aiProviders: async () => {
        const rows = await request<
          Array<{
            id: string;
            displayName: string;
            isActive: boolean;
            models?: unknown;
          }>
        >("/v1/platform/ai/providers");
        return rows.map((r): AiProviderRow => {
          const modelsRaw = Array.isArray(r.models) ? r.models : [];
          return {
            id: r.id,
            name: r.displayName,
            enabled: r.isActive,
            models: modelsRaw.map((m, i) => {
              if (m && typeof m === "object") {
                const row = m as Record<string, unknown>;
                return {
                  id: String(row.id ?? row.modelId ?? `model-${i}`),
                  name: String(row.name ?? row.displayName ?? row.id ?? `model-${i}`),
                  enabled: row.isEnabled !== false && row.enabled !== false,
                };
              }
              return {
                id: `model-${i}`,
                name: String(m),
                enabled: true,
              };
            }),
          };
        });
      },
      connectorCatalog: async () => {
        throw new ApiClientError(
          "NOT_IMPLEMENTED",
          "Connector catalog API is out of scope for this Profile.",
          501,
        );
      },
      riskPanel: async () => {
        throw new ApiClientError(
          "NOT_IMPLEMENTED",
          "Platform risk panel API is out of scope for this Profile.",
          501,
        );
      },
      tripCircuit: async () => {
        throw new ApiClientError(
          "NOT_IMPLEMENTED",
          "Platform risk trip API is out of scope for this Profile.",
          501,
        );
      },
    },
    logistics: {
      dashboard: (query?: {
        departmentId?: string;
        logisticsBranchId?: string;
      }) => {
        const params = new URLSearchParams();
        if (query?.departmentId) params.set("departmentId", query.departmentId);
        if (query?.logisticsBranchId) {
          params.set("logisticsBranchId", query.logisticsBranchId);
        }
        const qs = params.toString() ? `?${params}` : "";
        return request<LogisticsDashboardStats>(`/v1/logistics/dashboard${qs}`);
      },
      clientDashboard: () =>
        request<ClientDashboardStats>("/v1/dashboard/client"),
      exceptions: (query?: {
        page?: number;
        pageSize?: number;
        logisticsBranchId?: string;
      }) => {
        const params = new URLSearchParams();
        if (query?.page != null) params.set("page", String(query.page));
        if (query?.pageSize != null) {
          params.set("pageSize", String(query.pageSize));
        }
        if (query?.logisticsBranchId) {
          params.set("logisticsBranchId", query.logisticsBranchId);
        }
        const qs = params.toString() ? `?${params}` : "";
        return request<LogisticsExceptionsResponse>(
          `/v1/logistics/exceptions${qs}`,
        );
      },
      alerts: () =>
        request<LogisticsAlertsResponse>("/v1/logistics/alerts"),
      departments: () =>
        request<LogisticsDepartmentOption[]>("/v1/logistics/departments"),
      teams: () =>
        request<TeamOption[]>("/v1/logistics/teams"),
      branches: async () => {
        const rows = await request<
          Array<{
            id: string;
            name: string;
            slug: string;
            isDefault: boolean;
          }>
        >("/v1/logistics/branches");
        return rows.map(
          (w): WorkspaceSummary => ({
            id: w.id,
            name: w.name,
            slug: w.slug,
            isDefault: w.isDefault,
            archivedAt: null,
          }),
        );
      },
      orderServices: {
        list: async (query?: {
          page?: number;
          pageSize?: number;
          q?: string;
          filter?: OrderServiceListFilter;
          logisticsBranchId?: string;
          status?: string;
          clientOrganizationId?: string;
          storeId?: string;
          endCustomerId?: string;
          sort?: OrderServiceListSortField;
          order?: "asc" | "desc";
        }): Promise<OrderServiceListPage> => {
          const mapped = filterToListQuery(query?.filter);
          const params = new URLSearchParams();
          params.set("page", String(query?.page ?? 1));
          params.set("pageSize", String(query?.pageSize ?? 25));
          if (mapped.filter && mapped.filter !== "all") {
            params.set("filter", mapped.filter);
          }
          if (mapped.status) params.set("status", mapped.status);
          else if (query?.status) params.set("status", query.status);
          if (mapped.overdue) params.set("overdue", "true");
          if (mapped.assignee) params.set("assignee", mapped.assignee);
          if (query?.logisticsBranchId) {
            params.set("logisticsBranchId", query.logisticsBranchId);
          }
          if (query?.clientOrganizationId) {
            params.set("clientOrganizationId", query.clientOrganizationId);
          }
          if (query?.storeId) params.set("storeId", query.storeId);
          if (query?.endCustomerId) {
            params.set("endCustomerId", query.endCustomerId);
          }
          if (query?.q?.trim()) params.set("q", query.q.trim());
          if (query?.sort) params.set("sort", query.sort);
          if (query?.order) params.set("order", query.order);
          const res = await request<OrderServiceListResponse>(
            `/v1/order-services?${params}`,
          );
          return {
            items: res.items as OrderServiceListRow[],
            total: res.total,
            page: res.page,
            pageSize: res.pageSize,
          };
        },
        billingUploadLookup: (q: string) => {
          const params = new URLSearchParams();
          if (q.trim()) params.set("q", q.trim());
          return request<BillingUploadLookupResponse>(
            `/v1/order-services/billing-upload/lookup?${params}`,
          );
        },
        get: async (id: string) => {
          const os = await request<OrderService>(`/v1/order-services/${id}`);
          return mapOrderToDetailView(os);
        },
        access: {
          get: (id: string) =>
            request<OrderServiceAccessView>(`/v1/order-services/${id}/access`),
          addGrant: (
            id: string,
            body: {
              principalType: "USER" | "TEAM";
              principalId: string;
              principalRealm: "staff" | "client";
            },
          ) =>
            request<OrderServiceAccessGrant>(
              `/v1/order-services/${id}/access/grants`,
              { method: "POST", body: JSON.stringify(body) },
            ),
          removeGrant: async (
            id: string,
            grantId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/order-services/${id}/access/grants/${grantId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
        },
        create: async (body: CreateOrderServiceInput) => {
          const os = await request<OrderService>("/v1/order-services", {
            method: "POST",
            body: JSON.stringify(body),
          });
          return mapOrderToDetailView(os);
        },
        update: async (
          id: string,
          body: {
            version: number;
            storeId?: string | null;
            logisticsBranchId?: string;
            customerReference?: string | null;
            shipmentReference?: string | null;
            shipmentInfo?: Record<string, unknown> | null;
            warehouse?: string | null;
            dueDate?: string | null;
            priority?: string;
            remarks?: string | null;
            billingAccountId?: string | null;
            billingScope?: string | null;
            billingReference?: string | null;
          },
        ) => {
          const os = await request<OrderService>(`/v1/order-services/${id}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          });
          return mapOrderToDetailView(os);
        },
        submit: async (id: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/submit`,
            { method: "POST" },
          );
          return mapOrderToDetailView(os);
        },
        advanceStep: async (
          id: string,
          stepId: string,
          input?: { toStatus?: string; reason?: string },
        ) => {
          const toStatus = input?.toStatus;
          if (!toStatus) {
            throw new ApiClientError(
              "VALIDATION_FAILED",
              "toStatus is required to advance a workflow step.",
              400,
            );
          }
          await request(
            `/v1/order-services/${id}/workflow/steps/${stepId}/status`,
            {
              method: "POST",
              body: JSON.stringify({
                toStatus,
                ...(input?.reason ? { reason: input.reason } : {}),
              }),
            },
          );
          let os = await request<OrderService>(`/v1/order-services/${id}`);
          // First real step work: SUBMITTED → PROCESSING (E.1).
          if (os.status === "SUBMITTED" && toStatus === "IN_PROGRESS") {
            try {
              os = await request<OrderService>(`/v1/order-services/${id}/status`, {
                method: "POST",
                body: JSON.stringify({ toStatus: "PROCESSING" }),
              });
            } catch {
              /* keep SUBMITTED if transition denied */
            }
          }
          return mapOrderToDetailView(os);
        },
        hold: async (id: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/hold`,
            { method: "POST" },
          );
          return mapOrderToDetailView(os);
        },
        resume: async (id: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/status`,
            {
              method: "POST",
              body: JSON.stringify({ toStatus: "PROCESSING" }),
            },
          );
          return mapOrderToDetailView(os);
        },
        complete: async (id: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/complete`,
            { method: "POST" },
          );
          return mapOrderToDetailView(os);
        },
        cancel: async (id: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/cancel`,
            { method: "POST" },
          );
          return mapOrderToDetailView(os);
        },
        reject: async (id: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/reject`,
            { method: "POST" },
          );
          return mapOrderToDetailView(os);
        },
        close: async (
          id: string,
          body?: {
            overrideMissingMandatoryDocuments?: boolean;
            overrideReason?: string;
          },
        ) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/close`,
            {
              method: "POST",
              body: JSON.stringify(body ?? {}),
            },
          );
          return mapOrderToDetailView(os);
        },
        adminReopen: async (id: string, reason: string) => {
          const os = await request<OrderService>(
            `/v1/order-services/${id}/admin-reopen`,
            {
              method: "POST",
              body: JSON.stringify({ reason }),
            },
          );
          return mapOrderToDetailView(os);
        },
        assignStep: async (
          instanceId: string,
          stepId: string,
          input: AssignStepInput,
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}/assign`,
            {
              method: "POST",
              body: JSON.stringify(input),
            },
          );
        },
        startStep: async (instanceId: string, stepId: string) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}/status`,
            {
              method: "POST",
              body: JSON.stringify({ toStatus: "IN_PROGRESS" }),
            },
          );
        },
        updateStepProperties: async (
          instanceId: string,
          stepId: string,
          input: {
            name: string;
            isRequired?: boolean;
            slaExpectedMinutes: number | null;
            slaWarningMinutes: number | null;
            approvalRequired: boolean;
            clientActionRequired: boolean;
            clientConsentRequired?: boolean;
            canSkip: boolean;
            canReopen: boolean;
            canRepeat: boolean;
            parallelGroupKey?: string | null;
            documentTypeRequirements: Array<{ documentTypeId: string; mandatory: boolean }> | null;
          },
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}/properties`,
            { method: "PATCH", body: JSON.stringify(input) },
          );
        },
        skipStep: async (
          instanceId: string,
          stepId: string,
          input: {
            reason: string;
            mandatoryOverrideAuthorized?: boolean;
          },
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}/skip`,
            {
              method: "POST",
              body: JSON.stringify(input),
            },
          );
        },
        reopenStep: async (
          instanceId: string,
          stepId: string,
          reason?: string,
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}/reopen`,
            {
              method: "POST",
              body: JSON.stringify({ reason: reason ?? undefined }),
            },
          );
        },
        repeatStep: async (
          instanceId: string,
          stepId: string,
          placeAdjacent?: boolean,
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}/repeat`,
            {
              method: "POST",
              body: JSON.stringify({ placeAdjacent: placeAdjacent ?? true }),
            },
          );
        },
        addInstanceStep: async (
          instanceId: string,
          body: {
            name: string;
            sequence?: number;
            isRequired?: boolean;
            clientActionRequired?: boolean;
            clientConsentRequired?: boolean;
            approvalRequired?: boolean;
            canSkip?: boolean;
            canReopen?: boolean;
            canRepeat?: boolean;
            slaExpectedMinutes?: number | null;
            slaWarningMinutes?: number | null;
            parallelGroupKey?: string | null;
            dependsOnStepIds?: string[];
            documentTypeRequirements?: Array<{ documentTypeId: string; mandatory: boolean }> | null;
          },
        ) => {
          await request(`/v1/workflow-instances/${instanceId}/steps`, {
            method: "POST",
            body: JSON.stringify(body),
          });
        },
        removeInstanceStep: async (
          instanceId: string,
          stepId: string,
          mandatoryOverrideAuthorized?: boolean,
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/${stepId}`,
            {
              method: "DELETE",
              body: JSON.stringify({
                mandatoryOverrideAuthorized:
                  mandatoryOverrideAuthorized ?? false,
              }),
            },
          );
        },
        reorderInstanceSteps: async (
          instanceId: string,
          stepIds: string[],
        ) => {
          await request(
            `/v1/workflow-instances/${instanceId}/steps/reorder`,
            {
              method: "PATCH",
              body: JSON.stringify({ stepIds }),
            },
          );
        },
        listChecklist: (orderServiceId: string) =>
          request<ChecklistItemView[]>(
            `/v1/order-services/${orderServiceId}/checklist`,
          ),
        addChecklistItem: (
          orderServiceId: string,
          body: {
            documentTypeId: string;
            label?: string | null;
            isMandatory?: boolean;
          },
        ) =>
          request<ChecklistItemView>(
            `/v1/order-services/${orderServiceId}/checklist`,
            { method: "POST", body: JSON.stringify(body) },
          ),
        removeChecklistItem: (orderServiceId: string, itemId: string) =>
          request<{ ok: true }>(
            `/v1/order-services/${orderServiceId}/checklist/${itemId}`,
            { method: "DELETE" },
          ),
        updateChecklistItem: (
          orderServiceId: string,
          itemId: string,
          body: { remarksText?: string | null },
        ) =>
          request<ChecklistItemView>(
            `/v1/order-services/${orderServiceId}/checklist/${itemId}`,
            { method: "PATCH", body: JSON.stringify(body) },
          ),
      },
      documents: {
        uploadIntent: (body: DocumentUploadIntentInput) =>
          request<DocumentUploadIntentResult>("/v1/documents/upload-intent", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        uploadConfirm: (body: DocumentUploadConfirmInput) =>
          request<{ document: UploadedDocumentRow }>(
            "/v1/documents/upload-confirm",
            { method: "POST", body: JSON.stringify(body) },
          ),
        verifyChecklistItem: (orderServiceId: string, itemId: string) =>
          request(
            `/v1/order-services/${orderServiceId}/checklist/${itemId}/verify`,
            { method: "POST" },
          ),
        release: (documentId: string) =>
          request(`/v1/documents/${documentId}/release`, { method: "POST" }),
        returnToUploader: (documentId: string, reason: string) =>
          request(`/v1/documents/${documentId}/return`, {
            method: "POST",
            body: JSON.stringify({ reason }),
          }),
        rejectChecklistItem: (
          orderServiceId: string,
          itemId: string,
          body?: { reason?: string | null },
        ) =>
          request(
            `/v1/order-services/${orderServiceId}/checklist/${itemId}/reject`,
            {
              method: "POST",
              body: JSON.stringify(body ?? {}),
            },
          ),
        listByOrderService: async (
          orderServiceId: string,
          query?: { page?: number; pageSize?: number },
        ) => {
          const params = new URLSearchParams();
          params.set("orderServiceId", orderServiceId);
          if (query?.page != null) params.set("page", String(query.page));
          if (query?.pageSize != null) {
            params.set("pageSize", String(query.pageSize));
          }
          return request<{
            items: Array<{
              id: string;
              filename: string;
              mimeType: string;
              size: number;
              orderServiceId: string | null;
              workflowStepId: string | null;
              createdAt: string;
              documentTypeId: string;
              documentTypeName?: string | null;
              documentTypeCategory?: string | null;
              uploadedByName?: string | null;
              checklistItemId?: string | null;
              checklistStatus?: string | null;
              masterDocumentId?: string | null;
              version: number;
              isCurrent?: boolean;
              uploadedById?: string | null;
              clientVisibility?: "INTERNAL" | "PUBLISHED" | null;
              canRelease?: boolean;
              releaseReturnReason?: string | null;
              kind: string;
            }>;
            total: number;
            page: number;
            pageSize: number;
          }>(`/v1/documents?${params}`);
        },
        listMaster: async (
          clientOrganizationId: string,
          query?: {
            endCustomerId?: string;
            cursor?: string;
            limit?: number;
          },
        ): Promise<CursorListPage<MasterDocumentRow>> => {
          const params = new URLSearchParams();
          params.set("clientOrganizationId", clientOrganizationId);
          if (query?.endCustomerId) {
            params.set("endCustomerId", query.endCustomerId);
          }
          if (query?.cursor) params.set("cursor", query.cursor);
          const fallbackLimit = query?.limit ?? 25;
          if (query?.limit != null) params.set("limit", String(query.limit));
          const { data, pagination } = await requestWithMeta<
            Array<{
              id: string;
              clientOrganizationId: string;
              endCustomerId?: string | null;
              documentTypeId: string;
              filename: string;
              mimeType: string;
              size: number;
              version: number;
              issueDate: string | null;
              expiryDate: string | null;
              createdAt: string;
              uploadedById?: string | null;
              clientVisibility?: "INTERNAL" | "PUBLISHED" | null;
              canRelease?: boolean;
              releaseReturnReason?: string | null;
            }>
          >(`/v1/documents/master?${params}`);
          const items = (data ?? []).map(
            (r): MasterDocumentRow => ({
              id: r.id,
              clientOrganizationId: r.clientOrganizationId,
              endCustomerId: r.endCustomerId ?? null,
              documentTypeId: r.documentTypeId,
              filename: r.filename,
              mimeType: r.mimeType,
              size: r.size,
              version: r.version,
              issueDate: r.issueDate,
              expiryDate: r.expiryDate,
              createdAt: r.createdAt,
              uploadedById: r.uploadedById ?? null,
              clientVisibility: r.clientVisibility ?? null,
              canRelease: r.canRelease ?? false,
              releaseReturnReason: r.releaseReturnReason ?? null,
            }),
          );
          return cursorPageFromMeta(items, pagination, fallbackLimit);
        },
        masterUploadIntent: (body: {
          clientOrganizationId: string;
          endCustomerId?: string | null;
          documentTypeId: string;
          filename: string;
          mimeType: string;
          size: number;
          issueDate?: string | null;
          expiryDate?: string | null;
        }) =>
          request<DocumentUploadIntentResult>("/v1/documents/master", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        linkMaster: (
          masterId: string,
          body: {
            orderServiceId: string;
            checklistItemId?: string | null;
            workflowStepId?: string | null;
          },
        ) =>
          request<{ document: UploadedDocumentRow; expiryWarning: boolean }>(
            `/v1/documents/master/${masterId}/link`,
            { method: "POST", body: JSON.stringify(body) },
          ),
        downloadUrl: (id: string) =>
          request<{
            documentId: string;
            downloadUrl: string;
            expiresIn: number;
            mimeType: string;
          }>(`/v1/documents/${id}/download-url`),
        previewUrl: (id: string) =>
          request<{
            documentId: string;
            previewUrl: string | null;
            expiresIn: number | null;
            mimeType: string;
            supported: boolean;
          }>(`/v1/documents/${id}/preview-url`),
        get: (id: string) =>
          request<UploadedDocumentRow & { filename: string; mimeType: string }>(
            `/v1/documents/${id}`,
          ),
        listVersions: (id: string) =>
          request<
            Array<{
              id: string;
              version: number;
              filename: string;
              isCurrent: boolean;
              createdAt: string;
            }>
          >(`/v1/documents/${id}/versions`),
        softDelete: (id: string) =>
          request(`/v1/documents/${id}`, { method: "DELETE" }),
        newVersionIntent: (
          id: string,
          body: {
            orderServiceId?: string;
            workflowStepId?: string | null;
            checklistItemId?: string | null;
            documentTypeId: string;
            filename: string;
            mimeType: string;
            size: number;
            issueDate?: string | null;
            expiryDate?: string | null;
          },
        ) =>
          request<DocumentUploadIntentResult>(
            `/v1/documents/${id}/versions`,
            { method: "POST", body: JSON.stringify(body) },
          ),
      },
      clientOrganizations: {
        list: async (query?: {
          q?: string;
          engagement?: "DIRECT" | "AGENT";
          cursor?: string;
          limit?: number;
        }): Promise<CursorListPage<ClientOrganizationRow>> => {
          const params = new URLSearchParams();
          if (query?.q) params.set("q", query.q);
          if (query?.engagement) params.set("engagement", query.engagement);
          if (query?.cursor) params.set("cursor", query.cursor);
          if (query?.limit != null) params.set("limit", String(query.limit));
          const qs = params.toString() ? `?${params}` : "";
          const fallbackLimit = query?.limit ?? 200;
          const { data, pagination } =
            await requestWithMeta<ClientOrganizationRow[]>(
              `/v1/client-organizations${qs}`,
            );
          return cursorPageFromMeta(data ?? [], pagination, fallbackLimit);
        },
        create: (body: CorporateProfileInput & {
          name: string;
          engagement?: "DIRECT" | "AGENT";
        }) =>
          request<ClientOrganizationRow>("/v1/client-organizations", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        update: (
          id: string,
          body: CorporateProfileInput & {
            name?: string;
            engagement?: "DIRECT" | "AGENT";
            isActive?: boolean;
          },
        ) =>
          request<ClientOrganizationRow>(`/v1/client-organizations/${id}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          }),
        me: () => request<ClientOrgMembership>("/v1/client-organizations/me"),
        endCustomers: {
          list: async (
            clientOrganizationId: string,
            query?: { q?: string; cursor?: string; limit?: number },
          ): Promise<CursorListPage<EndCustomerRow>> => {
            const params = new URLSearchParams();
            if (query?.q) params.set("q", query.q);
            if (query?.cursor) params.set("cursor", query.cursor);
            if (query?.limit != null) params.set("limit", String(query.limit));
            const qs = params.toString() ? `?${params}` : "";
            const fallbackLimit = query?.limit ?? 200;
            const { data, pagination } = await requestWithMeta<EndCustomerRow[]>(
              `/v1/client-organizations/${clientOrganizationId}/end-customers${qs}`,
            );
            return cursorPageFromMeta(data ?? [], pagination, fallbackLimit);
          },
          create: (
            clientOrganizationId: string,
            body: CorporateProfileInput & { name: string; code?: string | null },
          ) =>
            request<EndCustomerRow>(
              `/v1/client-organizations/${clientOrganizationId}/end-customers`,
              { method: "POST", body: JSON.stringify(body) },
            ),
          update: (
            clientOrganizationId: string,
            endCustomerId: string,
            body: CorporateProfileInput & {
              name?: string;
              code?: string | null;
              isActive?: boolean;
            },
          ) =>
            request<EndCustomerRow>(
              `/v1/client-organizations/${clientOrganizationId}/end-customers/${endCustomerId}`,
              { method: "PATCH", body: JSON.stringify(body) },
            ),
          remove: async (
            clientOrganizationId: string,
            endCustomerId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/client-organizations/${clientOrganizationId}/end-customers/${endCustomerId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
        },
        members: {
          list: async (clientOrganizationId: string) => {
            const rows = await request<
              Array<{
                id: string;
                clientOrganizationId: string;
                userId: string;
                role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
                createdAt: string;
                endCustomerIds?: string[];
                storeIds?: string[];
                email?: string;
                name?: string;
              }>
            >(`/v1/client-organizations/${clientOrganizationId}/members`);
            return rows.map(
              (r): ClientOrganizationMemberRow => ({
                id: r.id,
                clientOrganizationId: r.clientOrganizationId,
                userId: r.userId,
                role: r.role,
                createdAt: r.createdAt,
                endCustomerIds: r.endCustomerIds ?? [],
                storeIds: r.storeIds ?? [],
                email: r.email ?? r.userId,
                name: r.name ?? r.email ?? r.userId,
              }),
            );
          },
          searchCandidates: (
            clientOrganizationId: string,
            query?: { q?: string },
          ) => {
            const qs = query?.q
              ? `?q=${encodeURIComponent(query.q)}`
              : "";
            return request<ClientUserCandidateRow[]>(
              `/v1/client-organizations/${clientOrganizationId}/members/candidates${qs}`,
            );
          },
          createAccount: (
            clientOrganizationId: string,
            body: {
              email: string;
              fullName: string;
              password: string;
              role: "CLIENT_ADMIN" | "CLIENT_STAFF" | "STORE_USER";
              endCustomerIds?: string[];
              storeIds?: string[];
            },
          ) =>
            request<ClientOrganizationMemberRow>(
              `/v1/client-organizations/${clientOrganizationId}/members/accounts`,
              { method: "POST", body: JSON.stringify(body) },
            ),
          create: (
            clientOrganizationId: string,
            body: CreateClientOrganizationMemberInput,
          ) =>
            request<ClientOrganizationMemberRow>(
              `/v1/client-organizations/${clientOrganizationId}/members`,
              { method: "POST", body: JSON.stringify(body) },
            ),
          update: (
            clientOrganizationId: string,
            memberId: string,
            body: UpdateClientOrganizationMemberInput,
          ) =>
            request<ClientOrganizationMemberRow>(
              `/v1/client-organizations/${clientOrganizationId}/members/${memberId}`,
              { method: "PATCH", body: JSON.stringify(body) },
            ),
          remove: async (
            clientOrganizationId: string,
            memberId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/client-organizations/${clientOrganizationId}/members/${memberId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
        },
        invitations: {
          list: (clientOrganizationId: string) =>
            request<ClientOrganizationInvitationRow[]>(
              `/v1/client-organizations/${clientOrganizationId}/invitations`,
            ),
          create: (
            clientOrganizationId: string,
            body: CreateClientOrganizationInvitationInput,
          ) =>
            request<ClientOrganizationInvitationRow>(
              `/v1/client-organizations/${clientOrganizationId}/invitations`,
              { method: "POST", body: JSON.stringify(body) },
            ),
          preview: (token: string) =>
            request<
              | { status: "valid"; organizationName: string }
              | { status: "expired"; organizationName: string }
              | { status: "invalid" }
            >(
              `/v1/client-organization-invitations/preview?token=${encodeURIComponent(token)}`,
            ),
          accept: (body: {
            token: string;
            password: string;
            fullName?: string;
          }) =>
            request<{ ok: true; userId: string }>(
              "/v1/client-organization-invitations/accept",
              { method: "POST", body: JSON.stringify(body) },
            ),
        },
        teamResponsibilities: {
          list: (clientOrganizationId: string) =>
            request<
              Array<{
                id: string;
                teamId: string;
                clientOrganizationId: string;
                createdAt: string;
                createdById: string | null;
                teamName?: string | null;
              }>
            >(
              `/v1/client-organizations/${clientOrganizationId}/team-responsibilities`,
            ),
          add: (clientOrganizationId: string, teamId: string) =>
            request<{
              id: string;
              teamId: string;
              clientOrganizationId: string;
              createdAt: string;
              createdById: string | null;
              teamName?: string | null;
            }>(
              `/v1/client-organizations/${clientOrganizationId}/team-responsibilities`,
              { method: "POST", body: JSON.stringify({ teamId }) },
            ),
          remove: async (
            clientOrganizationId: string,
            responsibilityId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/client-organizations/${clientOrganizationId}/team-responsibilities/${responsibilityId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
        },
        teams: {
          list: (clientOrganizationId: string) =>
            request<
              Array<{
                id: string;
                clientOrganizationId: string;
                name: string;
                description: string | null;
                createdAt: string;
                updatedAt: string;
                deletedAt: string | null;
                memberCount?: number;
                endCustomerIds?: string[];
              }>
            >(`/v1/client-organizations/${clientOrganizationId}/teams`),
          create: (
            clientOrganizationId: string,
            body: { name: string; description?: string | null },
          ) =>
            request(
              `/v1/client-organizations/${clientOrganizationId}/teams`,
              { method: "POST", body: JSON.stringify(body) },
            ),
          update: (
            clientOrganizationId: string,
            teamId: string,
            body: { name?: string; description?: string | null },
          ) =>
            request(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}`,
              { method: "PATCH", body: JSON.stringify(body) },
            ),
          remove: async (
            clientOrganizationId: string,
            teamId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
          listMembers: (clientOrganizationId: string, teamId: string) =>
            request<
              Array<{
                id: string;
                teamId: string;
                memberId: string;
                userId?: string;
                email?: string;
                name?: string | null;
                role?: string;
                createdAt: string;
              }>
            >(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}/members`,
            ),
          addMember: (
            clientOrganizationId: string,
            teamId: string,
            memberId: string,
          ) =>
            request(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}/members`,
              { method: "POST", body: JSON.stringify({ memberId }) },
            ),
          removeMember: async (
            clientOrganizationId: string,
            teamId: string,
            teamMemberId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}/members/${teamMemberId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
          addEndCustomer: (
            clientOrganizationId: string,
            teamId: string,
            endCustomerId: string,
          ) =>
            request(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}/end-customers/${endCustomerId}`,
              { method: "POST" },
            ),
          removeEndCustomer: async (
            clientOrganizationId: string,
            teamId: string,
            endCustomerId: string,
          ): Promise<{ ok: true }> => {
            await request<void>(
              `/v1/client-organizations/${clientOrganizationId}/teams/${teamId}/end-customers/${endCustomerId}`,
              { method: "DELETE" },
            );
            return { ok: true };
          },
        },
      },
      stores: {
        list: async (query?: {
          q?: string;
          clientOrganizationId?: string;
          endCustomerId?: string;
          cursor?: string;
          limit?: number;
        }): Promise<CursorListPage<StoreRow>> => {
          const params = new URLSearchParams();
          if (query?.q) params.set("q", query.q);
          if (query?.clientOrganizationId) {
            params.set("clientOrganizationId", query.clientOrganizationId);
          }
          if (query?.endCustomerId) {
            params.set("endCustomerId", query.endCustomerId);
          }
          if (query?.cursor) params.set("cursor", query.cursor);
          if (query?.limit != null) params.set("limit", String(query.limit));
          const qs = params.toString() ? `?${params}` : "";
          const fallbackLimit = query?.limit ?? 200;
          const { data, pagination } = await requestWithMeta<
            Array<{
              id: string;
              name: string;
              code: string | null;
              address?: string | null;
              addressDetails?: BusinessAddress | null;
              businessEmail?: string | null;
              supportPhone?: string | null;
              clientOrganizationId: string;
              endCustomerId?: string | null;
              endCustomerName?: string | null;
              clientOrganizationName: string;
              status: "active" | "inactive";
              isActive?: boolean;
            }>
          >(`/v1/stores${qs}`);
          const items = (data ?? []).map(
            (r): StoreRow => ({
              id: r.id,
              name: r.name,
              code: r.code ?? "",
              address: r.address ?? null,
              addressDetails: r.addressDetails ?? null,
              businessEmail: r.businessEmail ?? null,
              supportPhone: r.supportPhone ?? null,
              clientOrganizationId: r.clientOrganizationId,
              endCustomerId: r.endCustomerId ?? null,
              endCustomerName: r.endCustomerName ?? null,
              clientOrganizationName: r.clientOrganizationName,
              status:
                r.status ?? (r.isActive === false ? "inactive" : "active"),
            }),
          );
          return cursorPageFromMeta(items, pagination, fallbackLimit);
        },
        create: async (body: {
          clientOrganizationId: string;
          endCustomerId?: string | null;
          name: string;
          code?: string | null;
          address?: string | null;
          addressDetails?: BusinessAddress | null;
          businessEmail?: string | null;
          supportPhone?: string | null;
        }) => {
          const r = await request<{
            id: string;
            name: string;
            code: string | null;
            address?: string | null;
            addressDetails?: BusinessAddress | null;
            businessEmail?: string | null;
            supportPhone?: string | null;
            clientOrganizationId: string;
            clientOrganizationName: string;
            status: "active" | "inactive";
          }>("/v1/stores", {
            method: "POST",
            body: JSON.stringify(body),
          });
          return {
            id: r.id,
            name: r.name,
            code: r.code ?? "",
            address: r.address ?? null,
            addressDetails: r.addressDetails ?? null,
            businessEmail: r.businessEmail ?? null,
            supportPhone: r.supportPhone ?? null,
            clientOrganizationId: r.clientOrganizationId,
            endCustomerId:
              (r as { endCustomerId?: string | null }).endCustomerId ?? null,
            endCustomerName:
              (r as { endCustomerName?: string | null }).endCustomerName ?? null,
            clientOrganizationName: r.clientOrganizationName,
            status: r.status,
          } satisfies StoreRow;
        },
        update: async (
          id: string,
          body: {
            name?: string;
            code?: string | null;
            address?: string | null;
            addressDetails?: BusinessAddress | null;
            businessEmail?: string | null;
            supportPhone?: string | null;
            isActive?: boolean;
          },
        ) => {
          const r = await request<{
            id: string;
            name: string;
            code: string | null;
            address?: string | null;
            addressDetails?: BusinessAddress | null;
            businessEmail?: string | null;
            supportPhone?: string | null;
            clientOrganizationId: string;
            endCustomerId?: string | null;
            endCustomerName?: string | null;
            clientOrganizationName: string;
            status: "active" | "inactive";
          }>(`/v1/stores/${id}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          });
          return {
            id: r.id,
            name: r.name,
            code: r.code ?? "",
            address: r.address ?? null,
            addressDetails: r.addressDetails ?? null,
            businessEmail: r.businessEmail ?? null,
            supportPhone: r.supportPhone ?? null,
            clientOrganizationId: r.clientOrganizationId,
            endCustomerId: r.endCustomerId ?? null,
            endCustomerName: r.endCustomerName ?? null,
            clientOrganizationName: r.clientOrganizationName,
            status: r.status,
          } satisfies StoreRow;
        },
      },
      workflowTemplates: {
        list: async () => {
          const rows = await request<
            Array<{
              id: string;
              name: string;
              serviceTypeId: string | null;
              serviceTypeName?: string | null;
              version: number;
              isActive: boolean;
              steps?: unknown[];
              stepCount?: number;
            }>
          >("/v1/workflow-templates");
          return rows.map(
            (r): WorkflowTemplateRow => ({
              id: r.id,
              name: r.name,
              serviceTypeId: r.serviceTypeId ?? null,
              serviceTypeName:
                r.serviceTypeName ?? r.serviceTypeId ?? "—",
              stepCount: r.steps?.length ?? r.stepCount ?? 0,
              version: r.version,
              isActive: r.isActive,
            }),
          );
        },
        create: async (input: {
          name: string;
          serviceTypeId?: string | null;
          isActive?: boolean;
          steps?: WorkflowTemplateStepInput[];
        }) => {
          const r = await request<{
            id: string;
            name: string;
            serviceTypeId: string | null;
            serviceTypeName?: string | null;
            version: number;
            isActive: boolean;
            steps?: WorkflowTemplateStepApi[];
          }>("/v1/workflow-templates", {
            method: "POST",
            body: JSON.stringify(input),
          });
          return {
            id: r.id,
            name: r.name,
            serviceTypeId: r.serviceTypeId,
            serviceTypeName: r.serviceTypeName ?? r.serviceTypeId ?? "—",
            version: r.version,
            isActive: r.isActive,
            steps: (r.steps ?? []).map((s) => mapWorkflowTemplateStep(s, r.id)),
          } satisfies WorkflowTemplateDetail;
        },
        get: async (id: string) => {
          const r = await request<{
            id: string;
            name: string;
            serviceTypeId: string | null;
            serviceTypeName?: string | null;
            version: number;
            isActive: boolean;
            steps?: WorkflowTemplateStepApi[];
          }>(`/v1/workflow-templates/${id}`);
          return {
            id: r.id,
            name: r.name,
            serviceTypeId: r.serviceTypeId,
            serviceTypeName: r.serviceTypeName ?? r.serviceTypeId ?? "—",
            version: r.version,
            isActive: r.isActive,
            steps: (r.steps ?? []).map((s) => mapWorkflowTemplateStep(s, r.id)),
          } satisfies WorkflowTemplateDetail;
        },
        update: async (
          id: string,
          input: {
            name?: string;
            serviceTypeId?: string | null;
            isActive?: boolean;
            steps?: WorkflowTemplateStepInput[];
          },
        ) => {
          const r = await request<{
            id: string;
            name: string;
            serviceTypeId: string | null;
            serviceTypeName?: string | null;
            version: number;
            isActive: boolean;
            steps?: WorkflowTemplateStepApi[];
          }>(`/v1/workflow-templates/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          });
          return {
            id: r.id,
            name: r.name,
            serviceTypeId: r.serviceTypeId,
            serviceTypeName: r.serviceTypeName ?? r.serviceTypeId ?? "—",
            version: r.version,
            isActive: r.isActive,
            steps: (r.steps ?? []).map((s) => mapWorkflowTemplateStep(s, r.id)),
          } satisfies WorkflowTemplateDetail;
        },
      },
      documentTypes: {
        list: async () => {
          const rows = await request<
            Array<{
              id: string;
              code?: string | null;
              name: string;
              category: string;
              uiGroup?: string | null;
              isActive: boolean;
              isRemarksOnly?: boolean;
            }>
          >("/v1/document-types");
          return rows.map(
            (r): DocumentTypeRow => ({
              id: r.id,
              code: r.code?.trim() || r.uiGroup?.trim() || r.category,
              name: r.name,
              category: r.category,
              isActive: r.isActive,
              isRemarksOnly: r.isRemarksOnly === true,
            }),
          );
        },
        create: async (input: {
          name: string;
          category: "STANDARD" | "EXTRA";
          uiGroup?: string | null;
          isActive?: boolean;
        }) => {
          const r = await request<{
            id: string;
            code?: string | null;
            name: string;
            category: string;
            uiGroup?: string | null;
            isActive: boolean;
          }>("/v1/document-types", {
            method: "POST",
            body: JSON.stringify(input),
          });
          return {
            id: r.id,
            code: r.code?.trim() || r.uiGroup?.trim() || r.category,
            name: r.name,
            category: r.category,
            isActive: r.isActive,
          } satisfies DocumentTypeRow;
        },
        update: (
          id: string,
          input: {
            name?: string;
            category?: "STANDARD" | "EXTRA";
            uiGroup?: string | null;
            isActive?: boolean;
          },
        ) =>
          request(`/v1/document-types/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          }),
      },
      serviceTypes: {
        list: async () => {
          const rows = await request<
            Array<{
              id: string;
              code: string;
              name: string;
              isActive: boolean;
              defaultWorkflowTemplateId?: string | null;
              defaultWorkflowTemplateName?: string | null;
              runningNumberFormat: string;
            }>
          >("/v1/service-types");
          return rows.map(
            (r): ServiceTypeRow => ({
              id: r.id,
              code: r.code,
              name: r.name,
              defaultTemplateId: r.defaultWorkflowTemplateId ?? null,
              defaultTemplateName:
                r.defaultWorkflowTemplateName ??
                r.defaultWorkflowTemplateId ??
                null,
              runningNumberFormat: r.runningNumberFormat,
              isActive: r.isActive,
            }),
          );
        },
        create: async (input: {
          code: string;
          name: string;
          runningNumberFormat?: string;
          defaultWorkflowTemplateId?: string | null;
          isActive?: boolean;
        }) => {
          const r = await request<{
            id: string;
            code: string;
            name: string;
            isActive: boolean;
            defaultWorkflowTemplateId?: string | null;
            defaultWorkflowTemplateName?: string | null;
            runningNumberFormat: string;
          }>("/v1/service-types", {
            method: "POST",
            body: JSON.stringify(input),
          });
          return {
            id: r.id,
            code: r.code,
            name: r.name,
            defaultTemplateId: r.defaultWorkflowTemplateId ?? null,
            defaultTemplateName:
              r.defaultWorkflowTemplateName ??
              r.defaultWorkflowTemplateId ??
              null,
            runningNumberFormat: r.runningNumberFormat,
            isActive: r.isActive,
          } satisfies ServiceTypeRow;
        },
        update: (
          id: string,
          input: {
            code?: string;
            name?: string;
            runningNumberFormat?: string;
            defaultWorkflowTemplateId?: string | null;
            isActive?: boolean;
          },
        ) =>
          request(`/v1/service-types/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          }),
      },
      billingAccounts: {
        list: async (query?: {
          clientOrganizationId?: string;
          storeId?: string;
          activeOnly?: boolean;
        }) => {
          const params = new URLSearchParams();
          if (query?.clientOrganizationId) {
            params.set("clientOrganizationId", query.clientOrganizationId);
          }
          if (query?.storeId) params.set("storeId", query.storeId);
          if (query?.activeOnly) params.set("activeOnly", "true");
          const qs = params.toString() ? `?${params}` : "";
          const rows = await request<BillingAccountRow[]>(`/v1/billing${qs}`);
          return rows.map(
            (r): BillingAccountRow => ({
              ...r,
              createdAt:
                typeof r.createdAt === "string"
                  ? r.createdAt
                  : String(r.createdAt),
              updatedAt:
                typeof r.updatedAt === "string"
                  ? r.updatedAt
                  : String(r.updatedAt),
            }),
          );
        },
        create: (input: {
          clientOrganizationId: string;
          storeId?: string | null;
          name: string;
          externalReference?: string | null;
          scope: "ORGANIZATION" | "STORE";
          isActive?: boolean;
        }) =>
          request<BillingAccountRow>("/v1/billing", {
            method: "POST",
            body: JSON.stringify(input),
          }),
        update: (
          id: string,
          input: {
            name?: string;
            externalReference?: string | null;
            isActive?: boolean;
            scope?: "ORGANIZATION" | "STORE";
            storeId?: string | null;
          },
        ) =>
          request<BillingAccountRow>(`/v1/billing/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          }),
        deactivate: (id: string) =>
          request(`/v1/billing/${id}`, { method: "DELETE" }),
      },
      commentPresets: {
        list: async (query?: {
          activeOnly?: boolean;
        }): Promise<CommentPresetRow[]> => {
          const params = new URLSearchParams();
          if (query?.activeOnly) params.set("activeOnly", "true");
          const qs = params.toString() ? `?${params}` : "";
          const rows = await request<CommentPresetRow[]>(
            `/v1/comment-presets${qs}`,
          );
          return (rows ?? []).map((r) => ({
            ...r,
            kind: r.kind ?? null,
            isActive: r.isActive !== false,
            sortOrder: r.sortOrder ?? 0,
            deletedAt: r.deletedAt ?? null,
            createdAt:
              typeof r.createdAt === "string"
                ? r.createdAt
                : String(r.createdAt),
            updatedAt:
              typeof r.updatedAt === "string"
                ? r.updatedAt
                : String(r.updatedAt),
          }));
        },
        create: (input: {
          title: string;
          body: string;
          kind?: "COMMENT" | "DOCUMENT_REQUEST" | null;
          isActive?: boolean;
          sortOrder?: number;
        }) =>
          request<CommentPresetRow>("/v1/comment-presets", {
            method: "POST",
            body: JSON.stringify(input),
          }),
        update: (
          id: string,
          input: {
            title?: string;
            body?: string;
            kind?: "COMMENT" | "DOCUMENT_REQUEST" | null;
            isActive?: boolean;
            sortOrder?: number;
          },
        ) =>
          request<CommentPresetRow>(`/v1/comment-presets/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          }),
        remove: (id: string) =>
          request(`/v1/comment-presets/${id}`, { method: "DELETE" }),
      },
      comments: {
        list: async (query: {
          orderServiceId: string;
          stepId?: string;
          cursor?: string;
          limit?: number;
        }): Promise<CursorListPage<CommentRow>> => {
          const params = new URLSearchParams();
          params.set("orderServiceId", query.orderServiceId);
          if (query.stepId) params.set("stepId", query.stepId);
          if (query.cursor) params.set("cursor", query.cursor);
          const fallbackLimit = query.limit ?? 25;
          if (query.limit != null) params.set("limit", String(query.limit));
          const { data, pagination } = await requestWithMeta<CommentRow[]>(
            `/v1/comments?${params}`,
          );
          const items = (data ?? []).map(
            (c): CommentRow => ({
              ...c,
              isInternal: c.isInternal === true,
              createdAt:
                typeof c.createdAt === "string"
                  ? c.createdAt
                  : String(c.createdAt),
            }),
          );
          return cursorPageFromMeta(items, pagination, fallbackLimit);
        },
        create: (input: {
          orderServiceId: string;
          workflowStepId?: string | null;
          message: string;
          kind?: "COMMENT" | "DOCUMENT_REQUEST" | "CLOSE_REQUEST";
          /** Tenant Staff only; ignored for client realm. */
          isInternal?: boolean;
          checklistItemId?: string | null;
        }) =>
          request<CommentRow>("/v1/comments", {
            method: "POST",
            body: JSON.stringify(input),
          }),
        remove: (id: string) =>
          request(`/v1/comments/${id}`, { method: "DELETE" }),
      },
      approvals: {
        inbox: async (query?: {
          status?: string;
          orderServiceId?: string;
          cursor?: string;
          limit?: number;
        }): Promise<CursorListPage<ApprovalRow>> => {
          const params = new URLSearchParams();
          if (query?.status) params.set("status", query.status);
          if (query?.orderServiceId) {
            params.set("orderServiceId", query.orderServiceId);
          }
          if (query?.cursor) params.set("cursor", query.cursor);
          const fallbackLimit = query?.limit ?? 25;
          if (query?.limit != null) params.set("limit", String(query.limit));
          const qs = params.toString() ? `?${params}` : "";
          const { data, pagination } = await requestWithMeta<ApprovalRow[]>(
            `/v1/approvals${qs}`,
          );
          const items = (data ?? []).map(
            (a): ApprovalRow => ({
              ...a,
              createdAt:
                typeof a.createdAt === "string"
                  ? a.createdAt
                  : String(a.createdAt),
              updatedAt:
                typeof a.updatedAt === "string"
                  ? a.updatedAt
                  : String(a.updatedAt),
              decidedAt:
                a.decidedAt == null
                  ? null
                  : typeof a.decidedAt === "string"
                    ? a.decidedAt
                    : String(a.decidedAt),
            }),
          );
          return cursorPageFromMeta(items, pagination, fallbackLimit);
        },
        create: (input: {
          workflowStepId: string;
          orderServiceId: string;
          approverUserId?: string | null;
          approverRole?: string | null;
          submit?: boolean;
        }) =>
          request<ApprovalRow>("/v1/approvals", {
            method: "POST",
            body: JSON.stringify(input),
          }),
        submit: (id: string) =>
          request<ApprovalRow>(`/v1/approvals/${id}/submit`, {
            method: "POST",
          }),
        approve: (id: string) =>
          request<ApprovalRow>(`/v1/approvals/${id}/approve`, {
            method: "POST",
          }),
        reject: (id: string, reason: string) =>
          request<ApprovalRow>(`/v1/approvals/${id}/reject`, {
            method: "POST",
            body: JSON.stringify({ reason }),
          }),
        revision: (id: string) =>
          request<ApprovalRow>(`/v1/approvals/${id}/revision`, {
            method: "POST",
          }),
        resubmit: (id: string) =>
          request<ApprovalRow>(`/v1/approvals/${id}/resubmit`, {
            method: "POST",
          }),
      },
      clientConsents: {
        list: async (query?: {
          status?: string;
          orderServiceId?: string;
          cursor?: string;
          limit?: number;
        }): Promise<CursorListPage<ClientConsentRow>> => {
          const params = new URLSearchParams();
          if (query?.status) params.set("status", query.status);
          if (query?.orderServiceId) {
            params.set("orderServiceId", query.orderServiceId);
          }
          if (query?.cursor) params.set("cursor", query.cursor);
          const fallbackLimit = query?.limit ?? 25;
          if (query?.limit != null) params.set("limit", String(query.limit));
          const qs = params.toString() ? `?${params}` : "";
          const { data, pagination } = await requestWithMeta<ClientConsentRow[]>(
            `/v1/client-consents${qs}`,
          );
          return cursorPageFromMeta(data ?? [], pagination, fallbackLimit);
        },
        create: (input: {
          workflowStepId: string;
          orderServiceId: string;
          title: string;
          summary: string;
          expectedBusinessDays: number;
          penaltyTerms: string;
        }) =>
          request<ClientConsentRow>("/v1/client-consents", {
            method: "POST",
            body: JSON.stringify(input),
          }),
        withdraw: (id: string) =>
          request<ClientConsentRow>(`/v1/client-consents/${id}/withdraw`, {
            method: "POST",
          }),
        agree: (id: string) =>
          request<ClientConsentRow>(`/v1/client-consents/${id}/agree`, {
            method: "POST",
            body: JSON.stringify({ acknowledgePenalty: true }),
          }),
        reject: (id: string, reason: string) =>
          request<ClientConsentRow>(`/v1/client-consents/${id}/reject`, {
            method: "POST",
            body: JSON.stringify({ reason }),
          }),
      },
    },
    clientTickets: {
      list: async (query?: {
        q?: string;
        cursor?: string;
        limit?: number;
      }): Promise<TicketListPage> => {
        const params = new URLSearchParams();
        if (query?.q?.trim()) params.set("q", query.q.trim());
        if (query?.cursor) params.set("cursor", query.cursor);
        if (query?.limit != null) params.set("limit", String(query.limit));
        const qs = params.toString() ? `?${params}` : "";
        const res = await request<{
          items: Array<{
            id: string;
            subject: string;
            status: string;
            priority: string;
            requesterUserId: string;
            assigneeUserId: string | null;
            requesterName?: string;
            assigneeName?: string | null;
            updatedAt: string | Date;
          }>;
          nextCursor: string | null;
        }>(`/v1/client/tickets${qs}`);
        return {
          items: (res.items ?? []).map(
            (t): TicketRow => ({
              id: t.id,
              subject: t.subject,
              status: normalizeTicketStatus(t.status),
              priority: normalizeTicketPriority(t.priority),
              requesterName: t.requesterName ?? t.requesterUserId,
              assigneeUserId: t.assigneeUserId ?? null,
              assigneeName: t.assigneeName ?? t.assigneeUserId,
              updatedAt:
                typeof t.updatedAt === "string"
                  ? t.updatedAt
                  : new Date(t.updatedAt).toISOString(),
            }),
          ),
          nextCursor: res.nextCursor ?? null,
        };
      },
      get: (id: string) => getTicketDetail(id, `/v1/client/tickets/${id}`),
      create: (input: {
        subject: string;
        body?: string;
        priority?: string;
      }) =>
        request(`/v1/client/tickets`, {
          method: "POST",
          body: JSON.stringify(input),
        }),
      reply: async (id: string, input: { body: string }) => {
        await request(`/v1/client/tickets/${id}/messages`, {
          method: "POST",
          body: JSON.stringify(input),
        });
        return getTicketDetail(id, `/v1/client/tickets/${id}`);
      },
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export type { LogisticsDashboardStats, ClientDashboardStats, ClientOrgMembership };
